import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ChartRenderer } from '@/components/report-builder/ChartRenderer';
import { ForecastChart } from '@/components/explore/ForecastChart';
import { AdHocProposalCard } from '@/components/explore/AdHocProposalCard';
import DiagnosticCard from '@/components/explore/DiagnosticCard';
import { TableRenderer } from '@/components/report-builder/TableRenderer';
import { CertificationBadge } from '@/components/CertificationBadge';
import { pivotForChart } from './exploreUtils';
import { VizControls } from './VizControls';
import { makeFormatValue, formatUnit, unitAxisSuffix } from '@/lib/units';
import { resolveLabel } from '@/lib/fieldLabel';
import { formatDateByGrain, grainDimLabel } from '@/lib/dateGrain';
import {
  BarChart3, Save, Bell, TrendingUp, Search,
  MessageSquare, ArrowRight, Loader2, Cpu, ThumbsUp, ThumbsDown,
  Download, FileText, FileSpreadsheet, Code2, ExternalLink, BookOpen, Sparkles, RefreshCw, Pencil, Sigma, LayoutDashboard, Pin, ChevronDown, ChevronUp, Copy, Filter, X, Check, ShieldCheck, AlertTriangle, History, Clock, GitCompareArrows, Fingerprint, Waypoints, EyeOff, CheckCircle2, Compass, Lightbulb, ScrollText, Layers, Siren, HelpCircle
} from 'lucide-react';
import QuickEditFieldDrawer from './QuickEditFieldDrawer';
import RequestPiiAccessDialog from './RequestPiiAccessDialog';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { usePermissions } from '@/lib/permissions';
import { useTerms, getTerm } from '@/lib/branding';

// A dimension whose name/label matches this is treated as geographic → Auto Map.
const GEO_DIM_RE = /\b(state|province|country|nation|region|territory|jurisdiction|county|city|postal|zip|geograph)\b/i;

// Pin an answer (reproducibility): capture the plan + values now, then verify
// later that the number hasn't drifted. Trusted-AI Phase 1.
function AnswerPinButton({ result, chip }) {
  const [pinId, setPinId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [drift, setDrift] = useState(null); // null | {drift, total_delta_pct}
  const modelId = result?.answer_contract?.lineage?.model_id || result?.model_id;

  const doPin = async () => {
    setBusy(true);
    try {
      const res = await api.pinAnswer({ question: result?.question || '', model_id: modelId, result });
      setPinId(res.pin_id);
      toast.success('Answer pinned — verify anytime to check for drift');
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  const doVerify = async () => {
    setBusy(true);
    try {
      const res = await api.verifyAnswerPin(pinId);
      setDrift(res);
      if (res.drift) {
        const d = res.total_delta_pct != null ? ` (total ${res.total_delta_pct >= 0 ? '+' : ''}${res.total_delta_pct.toFixed(1)}%)` : '';
        toast.warning(`This answer has drifted since you pinned it${d}`);
      } else {
        toast.success('Verified — the numbers match your pinned answer');
      }
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  if (!modelId) return null;
  if (!pinId) {
    return (
      <button onClick={doPin} disabled={busy} className={`${chip} hover:bg-muted/60 cursor-pointer`} data-testid="answer-pin-btn" title="Pin this exact answer so you can verify later it hasn't drifted">
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Pin className="w-3 h-3" />} Pin
      </button>
    );
  }
  const driftCls = drift ? (drift.drift ? 'text-amber-700 dark:text-amber-300 bg-amber-500/10 border-amber-500/30' : 'text-emerald-700 dark:text-emerald-300 bg-emerald-500/10 border-emerald-500/30') : '';
  return (
    <button onClick={doVerify} disabled={busy} className={`${chip} ${driftCls} hover:bg-muted/60 cursor-pointer`} data-testid="answer-verify-btn" title="Re-run the pinned plan and compare to the pinned values">
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : (drift ? (drift.drift ? <AlertTriangle className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />) : <Pin className="w-3 h-3" />)}
      {drift ? (drift.drift ? 'Drifted' : 'Matches') : 'Verify pin'}
    </button>
  );
}


function ExportMenu({ data, dims, measures, allFields, answer, narrative, msgIdx, debugQuery, result, vizState = {} }) {
  const [open, setOpen] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [branding, setBranding] = useState(null);
  const [hasChart, setHasChart] = useState(false);
  const [pdfTitle, setPdfTitle] = useState('');
  const [pdfHeader, setPdfHeader] = useState('');
  const [pdfFooter, setPdfFooter] = useState('');
  const [pdfOpts, setPdfOpts] = useState({ chart: true, table: true, narrative: true, provenance: true, branding: true });
  const [pdfPreset, setPdfPreset] = useState(null);
  const [presetSaving, setPresetSaving] = useState(false);
  const [exportFormat, setExportFormat] = useState('pdf');
  const [exportBusy, setExportBusy] = useState(false);
  if (!data?.length) return null;

  const cleanCols = () => Object.keys(data[0]).filter(c => c !== '_muted' && c !== '_anomaly' && !c.startsWith('_suppress'));
  const dlBlob = (content, mime, filename) => {
    const blob = content instanceof Blob ? content : new Blob([content], { type: mime });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = filename; a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };
  const escHtml = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

  const exportCSV = () => {
    const cols = Object.keys(data[0]).filter(c => c !== '_muted' && c !== '_anomaly' && !c.startsWith('_suppress'));
    const lines = [cols.map(c => { const h = resolveLabel(allFields, c) || c; return h.includes(',') ? `"${h}"` : h; }).join(',')];
    data.forEach(row => lines.push(cols.map(c => { const v = row[c]; return v === null || v === undefined ? '' : typeof v === 'string' && v.includes(',') ? `"${v}"` : String(v); }).join(',')));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'explore_data.csv'; a.click();
    toast.success('Exported CSV');
    setOpen(false);
  };

  const exportExcel = async () => {
    try {
      const cols = Object.keys(data[0] || {}).filter(c => c !== '_muted' && c !== '_anomaly' && !c.startsWith('_suppress'));
      // Send rows keyed by display names so the sheet headers use metadata labels.
      const relabeled = data.map(row => { const o = {}; cols.forEach(c => { o[resolveLabel(allFields, c) || c] = row[c]; }); return o; });
      const blob = await api.exportExcel({ model_id: '', dimensions: dims, measures, title: 'Explore Export', raw_data: relabeled });
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'explore_data.xlsx'; a.click();
      toast.success('Exported Excel');
    } catch {
      exportCSV(); // Fallback to CSV
    }
    setOpen(false);
  };

  const exportJson = () => {
    const cols = cleanCols();
    const relabeled = data.map(row => { const o = {}; cols.forEach(c => { o[resolveLabel(allFields, c) || c] = row[c] ?? null; }); return o; });
    dlBlob(JSON.stringify(relabeled, null, 2), 'application/json', 'explore_data.json');
    toast.success('Exported JSON');
  };

  const exportXml = () => {
    const cols = cleanCols();
    const tagName = (c) => String(c).replace(/[^a-zA-Z0-9_]/g, '_').replace(/^(\d)/, '_$1');
    const rows = data.map(row => `  <row>\n${cols.map(c => {
      const label = resolveLabel(allFields, c) || c;
      const v = row[c]; const val = v === null || v === undefined ? '' : escHtml(v);
      return `    <${tagName(c)} label="${escHtml(label)}">${val}</${tagName(c)}>`;
    }).join('\n')}\n  </row>`).join('\n');
    const xml = `<?xml version="1.0" encoding="UTF-8"?>\n<data>\n${rows}\n</data>`;
    dlBlob(xml, 'application/xml', 'explore_data.xml');
    toast.success('Exported XML');
  };

  const generatePng = async () => {
    const png = await captureChartPng();
    if (!png) { toast.error('No chart in this result to export'); return; }
    dlBlob(await (await fetch(png)).blob(), 'image/png', 'explore_chart.png');
    toast.success('Exported PNG');
  };

  const generateHtml = async () => {
    const cols = cleanCols();
    const parts = [];
    if (pdfOpts.narrative && narrative) parts.push(`<p>${escHtml(narrative)}</p>`);
    if (pdfOpts.narrative && answer && String(answer) !== pdfTitle) parts.push(`<p><em>${escHtml(answer)}</em></p>`);
    if (pdfOpts.chart) { const png = await captureChartPng(); if (png) parts.push(`<div class="chart"><img src="${png}" alt="chart"/></div>`); }
    if (pdfOpts.table && data.length) {
      const head = cols.map(c => `<th>${escHtml(resolveLabel(allFields, c) || c)}</th>`).join('');
      const body = data.map(r => `<tr>${cols.map(c => `<td>${escHtml(r[c] ?? '')}</td>`).join('')}</tr>`).join('');
      parts.push(`<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`);
    }
    const accent = (pdfOpts.branding && branding?.primary_color) || '#4f46e5';
    const heading = pdfHeader || (pdfOpts.branding && branding?.company_name) || '';
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${escHtml(pdfTitle || 'Explore Export')}</title>
<style>body{font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif;color:#1a1a1a;max-width:900px;margin:32px auto;padding:0 24px}
h1{color:${accent};font-size:22px;margin:0 0 4px} .hdr{color:#666;font-size:12px;margin-bottom:16px}
img{max-width:100%;border:1px solid #eee;border-radius:8px} table{border-collapse:collapse;width:100%;font-size:12px;margin-top:16px}
th,td{border:1px solid #e5e5e5;padding:6px 8px;text-align:left} th{background:${accent}12;color:${accent}} .chart{margin:16px 0}
footer{margin-top:24px;color:#999;font-size:11px;border-top:1px solid #eee;padding-top:8px}</style></head>
<body>${heading ? `<div class="hdr">${escHtml(heading)}</div>` : ''}<h1>${escHtml(pdfTitle || 'Explore Export')}</h1>
${parts.join('\n')}${pdfFooter ? `<footer>${escHtml(pdfFooter)}</footer>` : ''}</body></html>`;
    dlBlob(html, 'text/html', 'explore_export.html');
    toast.success('Exported HTML');
  };

  const runExport = async () => {
    setExportBusy(true);
    try {
      if (exportFormat === 'pdf') { await generatePdf(); }
      else if (exportFormat === 'png') { await generatePng(); }
      else if (exportFormat === 'html') { await generateHtml(); }
      else if (exportFormat === 'csv') { exportCSV(); }
      else if (exportFormat === 'xml') { exportXml(); }
      else if (exportFormat === 'json') { exportJson(); }
      else if (exportFormat === 'excel') { await exportExcel(); }
      setPdfOpen(false);
    } catch (e) { toast.error(e.message || 'Export failed'); }
    finally { setExportBusy(false); }
  };

  const captureChartPng = async () => {
    try {
      const msgEl = document.querySelector(`[data-testid="msg-result-${msgIdx}"]`);
      const chartContainer = msgEl?.querySelector('[data-testid="explore-live-chart"]');
      if (!chartContainer) return '';
      // MapLibre map (basemap + points / choropleth) renders to a WebGL canvas —
      // capture it directly (preserveDrawingBuffer is enabled on the map).
      const mlCanvas = chartContainer.querySelector('canvas.maplibregl-canvas') || chartContainer.querySelector('[data-testid="maplibre-geo-chart"] canvas');
      if (mlCanvas) {
        try {
          const png = mlCanvas.toDataURL('image/png');
          if (png && png.length > 200) return png;
        } catch (e) { /* fall through to SVG path */ }
      }
      // Recharts (bar/line/area/pie) → rasterize the whole .recharts-wrapper
      // with html2canvas so the export includes the axes AND the colour legend
      // exactly as shown. Serialising just the <svg> dropped the HTML legend
      // and, worse, `querySelector('.recharts-surface')` grabbed a 14px
      // toolbar-icon surface — producing the garbled thumbnail users saw.
      const rwrap = chartContainer.querySelector('.recharts-wrapper');
      if (rwrap) {
        try {
          const html2canvas = (await import('html2canvas')).default;
          const canvas = await html2canvas(rwrap, { backgroundColor: '#ffffff', scale: 2, logging: false, useCORS: true });
          const png = canvas.toDataURL('image/png');
          if (png && png.length > 200) return png;
        } catch (e) { console.warn('html2canvas chart capture failed, falling back to svg', e); }
      }
      // Vector geo maps (react-simple-maps) / other SVG charts. The result card
      // embeds the viz toolbar (14px icon <svg>s, some with class
      // recharts-surface) in the SAME container, so pick the LARGEST svg by
      // rendered area — never a toolbar icon.
      const _svgs = Array.from(chartContainer.querySelectorAll('svg'));
      const svgEl = _svgs
        .map(s => ({ s, r: s.getBoundingClientRect() }))
        .sort((a, b) => (b.r.width * b.r.height) - (a.r.width * a.r.height))
        .map(o => o.s)[0];
      if (!svgEl) return '';
      const w = svgEl.getAttribute('width') || svgEl.getBoundingClientRect().width || 700;
      const h = svgEl.getAttribute('height') || svgEl.getBoundingClientRect().height || 350;
      const clone = svgEl.cloneNode(true);
      clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      clone.querySelectorAll('text').forEach(t => {
        const fill = t.getAttribute('fill');
        if (!fill || fill === 'currentColor' || fill === '#a1a1aa' || fill === 'var(--foreground)') t.setAttribute('fill', '#333');
      });
      clone.querySelectorAll('line').forEach(el => {
        const stroke = el.getAttribute('stroke');
        if (stroke && (stroke.includes('a1a1aa') || stroke === 'currentColor' || stroke.includes('var('))) el.setAttribute('stroke', '#ddd');
      });
      const svgData = new XMLSerializer().serializeToString(clone);
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const svgUrl = URL.createObjectURL(svgBlob);
      const img = new Image();
      return await new Promise((resolve) => {
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = Number(w) * 2; canvas.height = Number(h) * 2;
          const ctx = canvas.getContext('2d');
          ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          URL.revokeObjectURL(svgUrl); resolve(canvas.toDataURL('image/png'));
        };
        img.onerror = () => { URL.revokeObjectURL(svgUrl); resolve(''); };
        img.src = svgUrl;
      });
    } catch (e) { console.warn('chart capture failed', e); return ''; }
  };

  const openPdfDialog = async () => {
    setOpen(false);
    const msgEl = document.querySelector(`[data-testid="msg-result-${msgIdx}"]`);
    const chartEl = msgEl?.querySelector('[data-testid="explore-live-chart"] .recharts-surface')
      || msgEl?.querySelector('[data-testid="maplibre-geo-chart"] canvas')
      || msgEl?.querySelector('[data-testid="explore-live-chart"] svg');
    const chartExists = !!chartEl;
    setHasChart(chartExists);
    setPdfTitle(answer ? String(answer) : 'Explore Results');
    let b = branding;
    if (!b) {
      try { b = await api.getBranding(); } catch { b = {}; }
      // Prefer the applied/default theme's logo on exports when one is set.
      try { const dt = await api.getDefaultTheme(); if (dt && dt.logo_url) b = { ...(b || {}), theme_logo_url: dt.logo_url }; } catch { /* ignore */ }
      setBranding(b || {});
    }
    const appName = (b && (b.app_name || b.company_name)) || '';
    // Load the caller's saved preset (per-user) and apply their remembered
    // choices; fall back to sensible defaults when there's no preset yet.
    let preset = pdfPreset;
    if (preset === null) { try { preset = await api.getPdfExportPreset(); } catch { preset = {}; } setPdfPreset(preset || {}); }
    preset = preset || {};
    const hasPreset = preset && Object.keys(preset).length > 0;
    if (preset.format) setExportFormat(preset.format);
    if (hasPreset) {
      setPdfOpts({
        chart: chartExists && preset.chart !== false,
        table: preset.table !== false,
        narrative: preset.narrative !== false,
        provenance: preset.provenance !== false,
        branding: preset.branding !== false,
      });
      setPdfHeader(preset.header != null ? preset.header : appName);
      setPdfFooter(preset.footer != null ? preset.footer : (appName ? `${appName} · Confidential` : 'Confidential'));
    } else {
      setPdfHeader(appName);
      setPdfFooter(appName ? `${appName} · Confidential` : 'Confidential');
      setPdfOpts(o => ({ ...o, chart: chartExists }));
    }
    setPdfOpen(true);
  };

  const savePdfPreset = async () => {
    setPresetSaving(true);
    try {
      const preset = {
        chart: pdfOpts.chart, table: pdfOpts.table, narrative: pdfOpts.narrative,
        provenance: pdfOpts.provenance, branding: pdfOpts.branding,
        header: pdfHeader, footer: pdfFooter, format: exportFormat,
      };
      await api.savePdfExportPreset(preset);
      setPdfPreset(preset);
      toast.success('Export preset saved');
    } catch (e) {
      toast.error('Could not save preset');
    } finally { setPresetSaving(false); }
  };

  const provenanceParts = () => {
    const da = debugQuery?.drill_across;
    const parts = [];
    if (da) {
      const facts = (da.fact_labels && da.fact_labels.length ? da.fact_labels : da.facts) || [];
      const onDims = (da.dimension_labels && da.dimension_labels.length ? da.dimension_labels : da.dimensions) || [];
      if (facts.length) parts.push(`Combined from ${facts.join(' + ')}${onDims.length ? ` · matched on ${onDims.join(', ')}` : ''}`);
    }
    if (debugQuery?.collection) parts.push(`Source: ${debugQuery.collection}`);
    if (debugQuery?.executed_via) parts.push(`Executed via: ${debugQuery.executed_via}`);
    if (result?.date_interpretation?.summary) parts.push(`Date basis: ${result.date_interpretation.summary}`);
    return parts;
  };

  // Compliance footnote: names the uncertified/ad-hoc measures behind the chart,
  // so a reader knows the numbers aren't peer-reviewed. Empty when everything is certified.
  const certificationFootnote = () => {
    const prov = result?._measure_provenance || [];
    const uncertified = prov
      .filter(p => p.kind === 'uncertified' || p.kind === 'ai_calculation')
      .map(p => resolveLabel(allFields, p.name) || p.label || p.name);
    if (uncertified.length) {
      return `Note: this result includes uncertified measures (${uncertified.join(', ')}). These values are ad-hoc and not peer-reviewed or governed.`;
    }
    if (result?._provenance_summary?.adhoc) {
      return 'Note: these numbers are an ad-hoc calculation, not derived from a governed, certified measure.';
    }
    const cs = result?._certification_summary;
    if (cs && cs.total > 0 && !cs.all_certified) {
      const map = result?._certification || {};
      const names = (result?.measures || [])
        .filter(m => (map[m]?.status || 'draft') !== 'certified')
        .map(m => resolveLabel(allFields, m) || m);
      if (names.length) return `Note: this result includes uncertified measures (${names.join(', ')}). Values are not peer-reviewed.`;
    }
    return '';
  };

  const hexToRgb = (hex) => {
    const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(String(hex || '').trim());
    return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : [10, 51, 37];
  };
  const toPngDataUrl = (url) => new Promise((resolve) => {
    if (!url) return resolve('');
    try {
      const img = new Image(); img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const c = document.createElement('canvas'); c.width = img.naturalWidth || 64; c.height = img.naturalHeight || 64;
          c.getContext('2d').drawImage(img, 0, 0); resolve(c.toDataURL('image/png'));
        } catch { resolve(''); }
      };
      img.onerror = () => resolve('');
      img.src = url;
    } catch { resolve(''); }
  });

  const generatePdf = async () => {
    const { jsPDF } = await import('jspdf');
    const label = (c) => resolveLabel(allFields, c) || c;
    const cols = Object.keys(data[0] || {}).filter(c => c !== '_muted' && c !== '_anomaly' && !c.startsWith('_suppress'));
    const useB = pdfOpts.branding && branding;
    const [ar, ag, ab] = hexToRgb((useB && branding.accent_color) || '#0a3325');

    // Wide data tables don't fit A4 portrait — auto-switch the whole document to
    // landscape so all columns are readable. Portrait A4 content width ≈ 515pt;
    // assume ~80pt per column as a minimum legible width.
    const PORTRAIT_CONTENT_W = 595.28 - 80;
    const wideTable = pdfOpts.table && cols.length * 80 > PORTRAIT_CONTENT_W;
    const doc = new jsPDF({ orientation: wideTable ? 'landscape' : 'portrait', unit: 'pt', format: 'a4' });
    const PW = doc.internal.pageSize.getWidth();
    const PH = doc.internal.pageSize.getHeight();
    const M = 40, contentW = PW - M * 2, BOTTOM = PH - 40;
    let y = M;
    const ensureSpace = (h) => { if (y + h > BOTTOM) { doc.addPage(); y = M; } };
    const truncate = (txt, w) => {
      let s = String(txt == null ? '' : txt);
      if (doc.getTextWidth(s) <= w - 6) return s;
      while (s.length > 1 && doc.getTextWidth(s + '…') > w - 6) s = s.slice(0, -1);
      return s + '…';
    };

    if (pdfOpts.branding || pdfHeader) {
      let hx = M;
      if (useB) {
        const logoPng = await toPngDataUrl(branding.theme_logo_url || branding.logo_url || branding.logo || branding.favicon_url || '');
        if (logoPng) { try { doc.addImage(logoPng, 'PNG', M, y, 28, 28); hx = M + 36; } catch { /* skip */ } }
      }
      doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(ar, ag, ab);
      doc.text(pdfHeader || '', hx, y + 12);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(150);
      doc.text(new Date().toLocaleString(), hx, y + 24);
      y += 34;
      doc.setDrawColor(ar, ag, ab); doc.setLineWidth(2); doc.line(M, y, PW - M, y); y += 16;
    }

    doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.setTextColor(30);
    const titleLines = doc.splitTextToSize(pdfTitle || 'Explore Results', contentW);
    ensureSpace(titleLines.length * 16 + 4); doc.text(titleLines, M, y + 10); y += titleLines.length * 16 + 8;

    const addPara = (txt) => {
      if (!txt) return;
      doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(90);
      const lines = doc.splitTextToSize(String(txt), contentW);
      ensureSpace(lines.length * 12 + 6); doc.text(lines, M, y + 8); y += lines.length * 12 + 10;
    };
    if (pdfOpts.narrative) { addPara(narrative); if (answer && String(answer) !== pdfTitle) addPara(answer); }

    if (pdfOpts.chart) {
      const png = await captureChartPng();
      if (png) {
        try {
          const props = doc.getImageProperties(png);
          const w = contentW, h = Math.min(props.height * (w / props.width), 320);
          ensureSpace(h + 12); doc.addImage(png, 'PNG', M, y, w, h); y += h + 14;
        } catch { /* skip chart */ }
      }
      // Legend for geographic map exports so the shaded/sized values are readable.
      if (png && result?.chart_type === 'geo_map') {
        try {
          const gmode = vizState.geoMode || 'choropleth';
          const measure = (result?.calculations?.[0]?.name) || (result?.measures || [])[0];
          const vals = (data || []).map(r => Number(r[measure])).filter(v => Number.isFinite(v));
          if (measure && vals.length) {
            const mn = Math.min(...vals), mx = Math.max(...vals);
            const label = (resolveLabel(allFields, measure) || measure);
            const fmtS = (v) => { try { return Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }); } catch { return String(Math.round(v)); } };
            ensureSpace(48);
            doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(70);
            doc.text(label, M, y + 8); y += 12;
            doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(110);
            if (gmode === 'points') {
              const radii = [3, 6, 10]; const svals = [mn, (mn + mx) / 2, mx];
              let cx = M + 12;
              doc.setFillColor(20, 100, 244);
              radii.forEach((r, i) => { doc.circle(cx, y + 10, r, 'F'); doc.text(fmtS(svals[i]), cx, y + 26, { align: 'center' }); cx += 96; });
              y += 32;
            } else {
              const bx = M, bw = 200, bh = 10, seg = 32;
              for (let i = 0; i < seg; i++) { const t = seg === 1 ? 0 : i / (seg - 1); const a = 0.15 + t * 0.8; const rr = Math.round(255 * (1 - a) + 59 * a), gg = Math.round(255 * (1 - a) + 130 * a), bb = Math.round(255 * (1 - a) + 246 * a); doc.setFillColor(rr, gg, bb); doc.rect(bx + i * (bw / seg), y, (bw / seg) + 0.6, bh, 'F'); }
              doc.setDrawColor(200); doc.rect(bx, y, bw, bh);
              doc.text(fmtS(mn), bx, y + bh + 10);
              doc.text(fmtS(mx), bx + bw, y + bh + 10, { align: 'right' });
              y += bh + 18;
            }
            doc.setTextColor(0);
          }
        } catch { /* skip legend */ }
      }
    }

    // Forecast detail — projected periods + the model used per series, so the
    // PDF captures the numbers behind the chart, not just the picture.
    if (result?.output_type === 'forecast' && result?.forecast) {
      const fcst = result.forecast;
      const fmtNum = (v) => { try { return Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 }); } catch { return String(v); } };
      doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(ar, ag, ab);
      ensureSpace(20); doc.text('Forecast detail', M, y + 10); y += 18;
      if (fcst.multi) {
        (fcst.groups || []).forEach((g) => {
          ensureSpace(26);
          doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(40);
          doc.text(truncate(`${g.name} — model: ${g.model}`, contentW), M, y + 8); y += 12;
          const fut = (g.series || []).filter(s => s.type === 'forecast');
          if (fut.length) {
            doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(90);
            const line = fut.map(s => `${s.period}: ${fmtNum(s.value)}`).join('    ');
            const lines = doc.splitTextToSize(line, contentW - 8);
            ensureSpace(lines.length * 11 + 4); doc.text(lines, M + 8, y + 8); y += lines.length * 11 + 8;
          }
        });
      } else {
        doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(40);
        ensureSpace(14); doc.text(truncate(`Model: ${fcst.model}`, contentW), M, y + 8); y += 12;
        if (fcst.reason) {
          doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(90);
          const rl = doc.splitTextToSize(String(fcst.reason), contentW); ensureSpace(rl.length * 11 + 4);
          doc.text(rl, M, y + 8); y += rl.length * 11 + 6;
        }
        const fut = (fcst.series || []).filter(s => s.type === 'forecast');
        if (fut.length) {
          const fcols = ['Period', 'Projected', 'Low', 'High'];
          const cw = contentW / fcols.length, rh = 15;
          doc.setFillColor(ar, ag, ab); doc.rect(M, y, contentW, rh, 'F');
          doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(255);
          fcols.forEach((c, ci) => doc.text(c, M + ci * cw + 3, y + 10)); y += rh;
          doc.setFont('helvetica', 'normal'); doc.setTextColor(50);
          fut.forEach((s, ri) => {
            if (y + rh > BOTTOM) { doc.addPage(); y = M; }
            if (ri % 2 === 1) { doc.setFillColor(246, 246, 246); doc.rect(M, y, contentW, rh, 'F'); }
            const cells = [s.period, fmtNum(s.value), s.lower != null ? fmtNum(s.lower) : '', s.upper != null ? fmtNum(s.upper) : ''];
            cells.forEach((t, ci) => doc.text(truncate(String(t), cw), M + ci * cw + 3, y + 10));
            y += rh;
          });
          y += 6;
        }
      }
      if (fcst.dropped_periods && fcst.dropped_periods.length) {
        doc.setFont('helvetica', 'italic'); doc.setFontSize(8); doc.setTextColor(120);
        const dl = doc.splitTextToSize(`Excluded partial period(s): ${fcst.dropped_periods.join(', ')}.`, contentW);
        ensureSpace(dl.length * 11 + 4); doc.text(dl, M, y + 8); y += dl.length * 11 + 8;
        doc.setFont('helvetica', 'normal'); doc.setTextColor(0);
      }
    }


    // Uncertified-measures footnote — always included (compliance disclosure),
    // shown right under the chart/narrative regardless of the provenance toggle.
    {
      const certNote = certificationFootnote();
      if (certNote) {
        doc.setFont('helvetica', 'italic'); doc.setFontSize(8); doc.setTextColor(176, 108, 12);
        const lines = doc.splitTextToSize(certNote, contentW);
        ensureSpace(lines.length * 11 + 8);
        doc.text(lines, M, y + 8); y += lines.length * 11 + 10;
        doc.setFont('helvetica', 'normal'); doc.setTextColor(0);
      }
    }

    if (pdfOpts.provenance) {
      const parts = provenanceParts();
      if (parts.length) {
        const boxH = parts.length * 11 + 22;
        ensureSpace(boxH + 6);
        doc.setFillColor(245, 245, 245); doc.rect(M, y, contentW, boxH, 'F');
        doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(ar, ag, ab);
        doc.text('Data provenance', M + 8, y + 13);
        doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(90);
        parts.forEach((p, i) => doc.text(truncate('• ' + p, contentW - 16), M + 8, y + 26 + i * 11));
        y += boxH + 8;
      }
    }

    if (pdfOpts.table && cols.length) {
      const colW = contentW / cols.length, rowH = 16;
      const drawHeader = () => {
        doc.setFillColor(ar, ag, ab); doc.rect(M, y, contentW, rowH, 'F');
        doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(255);
        cols.forEach((c, ci) => doc.text(truncate(label(c), colW), M + ci * colW + 3, y + 11));
        y += rowH; doc.setFont('helvetica', 'normal'); doc.setTextColor(50);
      };
      ensureSpace(rowH * 2); drawHeader();
      data.slice(0, 500).forEach((row, ri) => {
        if (y + rowH > BOTTOM) { doc.addPage(); y = M; drawHeader(); }
        if (ri % 2 === 1) { doc.setFillColor(246, 246, 246); doc.rect(M, y, contentW, rowH, 'F'); }
        doc.setFontSize(8); doc.setTextColor(50);
        cols.forEach((c, ci) => {
          const v = row[c];
          const txt = v === null || v === undefined ? '' : (typeof v === 'number' ? v.toLocaleString() : String(v));
          doc.text(truncate(txt, colW), M + ci * colW + 3, y + 11);
        });
        y += rowH;
      });
    }

    const total = doc.getNumberOfPages();
    const ftxt = (pdfOpts.branding || pdfFooter) ? (pdfFooter || '') : '';
    for (let p = 1; p <= total; p++) {
      doc.setPage(p);
      doc.setDrawColor(220); doc.setLineWidth(0.5); doc.line(M, PH - 28, PW - M, PH - 28);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(150);
      if (ftxt) doc.text(ftxt, M, PH - 15);
      doc.text(`Page ${p} of ${total}`, PW - M, PH - 15, { align: 'right' });
    }

    doc.save(((pdfTitle || 'explore').replace(/[^\w]+/g, '_').slice(0, 40) || 'explore') + '.pdf');
    toast.success('PDF downloaded');
    setPdfOpen(false);
  };

  return (
    <div className="relative">
      <button onClick={openPdfDialog} className="p-1 rounded hover:bg-muted/30 text-muted-foreground/70 hover:text-foreground transition-colors" title="Export" data-testid="export-explore-btn">
        <Download className="w-3.5 h-3.5" />
      </button>
      {pdfOpen && createPortal(
        <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/50 p-4" onClick={() => setPdfOpen(false)} data-testid="pdf-export-dialog">
          <div className="bg-popover border border-border/50 rounded-xl shadow-2xl w-full max-w-sm max-h-[85vh] overflow-y-auto p-4" onClick={e => e.stopPropagation()}>
            <div className="text-sm font-semibold text-foreground mb-3">Export</div>
            <div className="mb-3">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">Format</div>
              <div className="grid grid-cols-4 gap-1.5">
                {[['pdf', 'PDF'], ['png', 'PNG'], ['html', 'HTML'], ['excel', 'Excel'], ['csv', 'CSV'], ['xml', 'XML'], ['json', 'JSON']].map(([id, label]) => {
                  const disabled = id === 'png' && !hasChart;
                  return (
                    <button key={id} disabled={disabled} onClick={() => setExportFormat(id)}
                      className={`px-2 py-1.5 rounded-md text-[11px] font-medium border transition-colors ${exportFormat === id ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted/40 text-foreground border-border/40 hover:bg-muted/60'} ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                      data-testid={`export-format-${id}`} title={disabled ? 'No chart in this result' : ''}>
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
            {['pdf', 'html'].includes(exportFormat) ? (
              <>
                <div className="space-y-2 mb-3">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Include</div>
                  {[['chart', 'Chart', hasChart], ['table', 'Data table', true], ['narrative', 'AI narrative', true], ['provenance', 'Provenance notes', exportFormat === 'pdf']].map(([id, label, enabled]) => (
                    <label key={id} className={`flex items-center gap-2 text-xs ${enabled ? 'text-foreground cursor-pointer' : 'text-muted-foreground/70 cursor-not-allowed'}`}>
                      <input type="checkbox" disabled={!enabled} checked={enabled && pdfOpts[id]} onChange={e => setPdfOpts(o => ({ ...o, [id]: e.target.checked }))} data-testid={`pdf-opt-${id}`} />
                      {label}{id === 'chart' && !hasChart ? ' (no chart in this result)' : ''}
                    </label>
                  ))}
                </div>
                <div className="space-y-2 mb-3 border-t border-border/30 pt-3">
                  <label className="flex items-center gap-2 text-xs text-foreground cursor-pointer font-medium">
                    <input type="checkbox" checked={pdfOpts.branding} onChange={e => setPdfOpts(o => ({ ...o, branding: e.target.checked }))} data-testid="pdf-opt-branding" />
                    Apply branding (logo, colors)
                  </label>
                  <div className="space-y-1.5">
                    <div>
                      <label className="text-[9px] uppercase tracking-wide text-muted-foreground block mb-0.5">Title</label>
                      <input value={pdfTitle} onChange={e => setPdfTitle(e.target.value)} placeholder="Report title" className="w-full text-xs rounded-md bg-muted/50 border border-border/40 px-2 py-1" data-testid="pdf-title-input" />
                    </div>
                    <div>
                      <label className="text-[9px] uppercase tracking-wide text-muted-foreground block mb-0.5">Header text</label>
                      <input value={pdfHeader} onChange={e => setPdfHeader(e.target.value)} placeholder="Shown top-left, next to the logo" className="w-full text-xs rounded-md bg-muted/50 border border-border/40 px-2 py-1" data-testid="pdf-header-input" />
                    </div>
                    <div>
                      <label className="text-[9px] uppercase tracking-wide text-muted-foreground block mb-0.5">Footer text</label>
                      <input value={pdfFooter} onChange={e => setPdfFooter(e.target.value)} placeholder="Shown at the bottom of every page" className="w-full text-xs rounded-md bg-muted/50 border border-border/40 px-2 py-1" data-testid="pdf-footer-input" />
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="mb-3 text-[11px] text-muted-foreground border-t border-border/30 pt-3">
                {exportFormat === 'png' ? 'Exports the chart image at 2× resolution.' : 'Exports the underlying data table (headers use display names).'}
              </div>
            )}
            <div className="flex items-center justify-between gap-2">
              {<button onClick={savePdfPreset} disabled={presetSaving} className="px-3 py-1.5 text-xs rounded-md text-primary hover:bg-primary/10 font-medium disabled:opacity-50" data-testid="pdf-save-preset" title="Remember this format and these choices for future exports">{presetSaving ? 'Saving…' : 'Save preset'}</button>}
              <div className="flex gap-2">
                <button onClick={() => setPdfOpen(false)} className="px-3 py-1.5 text-xs rounded-md text-muted-foreground hover:bg-muted/40" data-testid="pdf-cancel">Cancel</button>
                <button onClick={runExport} disabled={exportBusy} className="px-3 py-1.5 text-xs rounded-md bg-primary text-primary-foreground font-medium disabled:opacity-60" data-testid="pdf-generate">{exportBusy ? 'Exporting…' : `Export ${exportFormat.toUpperCase()}`}</button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// Combined clarify — two ambiguities asked in ONE card (no chaining). The user
// picks one option per group; Run composes base_question + the chosen patches.
function ClarifyGroups({ groups, baseQuestion, variant, modelId, onRun }) {
  const [sel, setSel] = React.useState({});
  const allChosen = groups.every((_, gi) => sel[gi] !== undefined);
  const run = () => {
    const patches = groups.map((g, gi) => g.options[sel[gi]]?.patch).filter(Boolean);
    groups.forEach((g, gi) => {
      const o = g.options[sel[gi]];
      if (o) { try { api.recordClarifyPick({ kind: o.kind || g.kind || 'unknown', label: o.label, variant, model_id: modelId }); } catch (e) { /* non-blocking */ } }
    });
    const finalQ = [baseQuestion, ...patches].filter(Boolean).join(' ').trim();
    onRun(finalQ);
  };
  return (
    <div className="mt-3 space-y-2.5" data-testid="clarification-groups">
      {groups.map((g, gi) => (
        <div key={gi} data-testid={`clarify-group-${g.kind}`}>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground/70 font-semibold mb-1.5">{g.title}</p>
          <div className="flex flex-wrap gap-1.5">
            {g.options.map((o, oi) => (
              <button
                key={oi}
                onClick={() => setSel(s => ({ ...s, [gi]: oi }))}
                className={`px-2.5 py-1 rounded-full border text-xs transition-colors ${sel[gi] === oi ? 'border-indigo-500/60 bg-indigo-500/15 text-indigo-700 dark:text-indigo-200' : 'border-border/40 bg-muted/20 text-muted-foreground hover:border-indigo-500/40'}`}
                data-testid={`clarify-group-${gi}-option-${oi}`}
              >{o.label}</button>
            ))}
          </div>
        </div>
      ))}
      <button
        onClick={run}
        disabled={!allChosen}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed active:scale-95 transition-transform"
        data-testid="clarify-groups-run"
      >Run</button>
    </div>
  );
}


// Best-effort parse of the generated SQL WHERE clause into human-readable
// filter chips (Type A). Handles = / != / <> / >,< / >=,<= / LIKE / ILIKE and
// IN / NOT IN, and merges same-column predicates (e.g. `col ILIKE 'a' OR col
// ILIKE 'b'`) into one chip. Best-effort — chips are a hint, never load-bearing.
function _cleanVal(t) {
  return String(t || '').trim().replace(/["'`]/g, '').replace(/^%+|%+$/g, '').trim();
}
function _cleanCol(t) {
  let s = String(t || '').trim().replace(/["'`]/g, '').trim();
  if (s.includes('.')) s = s.split('.').pop();
  return s.trim();
}
function parseSqlFilters(sql) {
  if (!sql || typeof sql !== 'string') return [];
  const m = sql.match(/\bWHERE\b([\s\S]*?)(\bGROUP\s+BY\b|\bORDER\s+BY\b|\bLIMIT\b|\bHAVING\b|;|$)/i);
  if (!m) return [];
  const clause = m[1];
  const re = /([\w".`]+(?:\.[\w".`]+)?)\s*(=|!=|<>|>=|<=|>|<|NOT\s+IN|IN|ILIKE|LIKE)\s*(\([^)]*\)|'[^']*'|"[^"]*"|[\w.%-]+)/gi;
  const byKey = {};
  const order = [];
  let mm;
  while ((mm = re.exec(clause)) !== null) {
    const col = _cleanCol(mm[1]);
    const op = mm[2].toUpperCase().replace(/\s+/g, ' ');
    const exclude = op === '!=' || op === '<>' || op === 'NOT IN';
    let values;
    if (mm[3].startsWith('(')) values = mm[3].slice(1, -1).split(',').map(_cleanVal).filter(Boolean);
    else values = [_cleanVal(mm[3])];
    const opWord = { '>': '>', '<': '<', '>=': '≥', '<=': '≤' }[op];
    const key = `${col}|${exclude ? 'ex' : 'in'}|${opWord || ''}`;
    if (!byKey[key]) { byKey[key] = { col, exclude, values: [], op: opWord }; order.push(key); }
    for (const v of values) if (v && !byKey[key].values.includes(v)) byKey[key].values.push(v);
  }
  return order.map(k => byKey[k]).filter(f => f.values.length).slice(0, 6);
}



export function ExploreMessage({
  msg, idx, messagesLength, user, isSharedSession,
  allFields, formatValue, sessionId, selectedModel,
  saveAsInsight, createAlert, runRCA, rcaLoading, runInvestigate, investigateLoading, resetInvestigation,
  addToDashboard,
  saveAsVisual,
  pinToKpi,
  setExploreQueryView, setQuestion, ask, setAiRawView, startFullInvestigation, startDiscussion, discEntry,
  onVizChange, onChangeGrain, onRerunQuery, onReaskAI, onRevisedResult, modelId, modelEditedAt, onModelEdited,
  isAdmin, onTryAdvancedSql, onBuildMeasure, onOpenAsOf, onDisambiguate
}) {
  useTerms();
  const navigate = useNavigate();
  const { can } = usePermissions();

  // ── Filters ──────────────────────────────────────────────────────────
  // Type A: "context" pills describing filters the natural-language query
  // already applied (parsed best-effort from the generated SQL WHERE clause).
  const queryFilters = useMemo(() => parseSqlFilters(msg.result?.sql), [msg.result?.sql]);
  // Ad-hoc = the AI built the metric itself. SQL-mode results carry an explicit
  // `ad_hoc` flag (+ formula); semantic-mode results expose a non-time-intel calc.
  const isAdHocMetric = useMemo(() => (
    msg.result?.ad_hoc === true || (msg.result?.calculations || []).some(c => c && !c.time_intel)
  ), [msg.result]);
  const adHocFormula = msg.result?.ad_hoc_formula || '';
  // Type B: an interactive, client-side filter the user adds on a dimension
  // that's on the chart, choosing which values to keep.
  const [bFilterField, setBFilterField] = useState(null);
  const [bFilterValues, setBFilterValues] = useState([]);
  const [showRemChange, setShowRemChange] = useState(false);
  const [fieldMenuOpen, setFieldMenuOpen] = useState(false);
  const [valueMenuOpen, setValueMenuOpen] = useState(false);
  const bFilterOptions = useMemo(() => {
    if (!bFilterField || !Array.isArray(msg.liveData)) return [];
    const seen = new Set();
    for (const r of msg.liveData) {
      const v = r[bFilterField];
      if (v !== undefined && v !== null) seen.add(String(v));
    }
    return Array.from(seen).sort().slice(0, 300);
  }, [bFilterField, msg.liveData]);
  const applyBFilter = useCallback((rows) => {
    if (!bFilterField || !bFilterValues.length || !Array.isArray(rows)) return rows;
    return rows.filter(r => bFilterValues.includes(String(r[bFilterField])));
  }, [bFilterField, bFilterValues]);
  const clearBFilter = () => { setBFilterField(null); setBFilterValues([]); setValueMenuOpen(false); };
  const toggleBValue = (v) => setBFilterValues(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]);
  // Filter Value Hints: show the actual values a field holds for an AI-applied
  // (Type A) value filter, so the user can swap to a different value quickly.
  const [valueHint, setValueHint] = useState({ open: null, loading: false, values: [], reason: '' });
  const openValueHint = async (f, fi) => {
    if (valueHint.open === fi) { setValueHint({ open: null, loading: false, values: [], reason: '' }); return; }
    setValueHint({ open: fi, loading: true, values: [], reason: '' });
    try {
      const res = await api.getFieldValues(modelId, f.col, '', 30);
      setValueHint({ open: fi, loading: false, values: res.values || [], reason: res.reason || '' });
    } catch {
      setValueHint({ open: fi, loading: false, values: [], reason: 'error' });
    }
  };
  const pickHintValue = (f, value) => {
    const label = resolveLabel(allFields, f.col) || f.col;
    const base = (msg.question || '').trim();
    const cur = (f.values && f.values[0]) || '';
    let prompt;
    if (base && cur && base.toLowerCase().includes(String(cur).toLowerCase())) {
      prompt = base.replace(new RegExp(String(cur).replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i'), value);
    } else {
      prompt = base ? `${base} where ${label} is ${value}` : `${label} is ${value}`;
    }
    setValueHint({ open: null, loading: false, values: [], reason: '' });
    setQuestion(prompt);
    setTimeout(() => ask(prompt), 60);
  };
  const lastHoverRef = useRef(null);
  const [quickEditOpen, setQuickEditOpen] = useState(false);
  // Fields (dims + measures) of this result, for the Quick-Edit drawer picker.
  const editableFields = [
    ...((msg.result?.dimensions) || []),
    ...((msg.result?.measures) || []),
  ].filter(Boolean).map(n => ({ name: n, label: resolveLabel(allFields, n) || n }));
  const needsResync = !!(onModelEdited && modelEditedAt && modelEditedAt > (msg._syncedAt || 0));
  const [feedbackState, setFeedbackState] = useState(null); // 'up' | 'down' | null
  const [downReason, setDownReason] = useState('');
  const [rejectCode, setRejectCode] = useState('');
  const [rejectOutcome, setRejectOutcome] = useState(null); // {kind:'dq'|'revision', plan_diff, result, message}
  const [showDownForm, setShowDownForm] = useState(false);
  const [reaskBase, setReaskBase] = useState(null); // when re-rejecting a revision, iterate on the latest revised result
  const [showRevSql, setShowRevSql] = useState(false); // reveal the revised answer's SQL
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [fixOutcome, setFixOutcome] = useState(null); // null | 'taught' | 'suggested' — thumbs-down "did this fix it?" result
  const [certRequested, setCertRequested] = useState({});
  const [certLoading, setCertLoading] = useState({});
  const [certifiedLocal, setCertifiedLocal] = useState({});
  const [provExpanded, setProvExpanded] = useState(false);
  const [showRepro, setShowRepro] = useState(false);
  const [trendOn, setTrendOn] = useState(false);
  const [trendSeries, setTrendSeries] = useState(null);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendUnavailable, setTrendUnavailable] = useState(false);
  const [trendMeta, setTrendMeta] = useState(null);
  const [anomalyStrictness, setAnomalyStrictness] = useState('balanced');
  const [anomalyDetail, setAnomalyDetail] = useState(null);
  const canCertify = ['admin', 'super_admin', 'tenant_admin', 'steward', 'data_steward', 'owner'].includes(user?.role);

  // Right-click on a data point (bar/point/slice, table row, map region) →
  // start a discussion pre-filled with that point's dimension/measure values.
  const capturePointPng = async () => {
    try {
      const msgEl = document.querySelector(`[data-testid="msg-result-${idx}"]`);
      const cc = msgEl?.querySelector('[data-testid="explore-live-chart"]') || msgEl?.querySelector('[data-testid="explore-live-table"]');
      if (!cc) return '';
      const canvas = cc.querySelector('canvas.maplibregl-canvas');
      if (canvas) { try { const p = canvas.toDataURL('image/png'); if (p && p.length > 200) return p; } catch { /* fall through */ } }
      const svgs = Array.from(cc.querySelectorAll('svg'));
      const svg = svgs.sort((a, b) => { const ra = a.getBoundingClientRect(), rb = b.getBoundingClientRect(); return (rb.width * rb.height) - (ra.width * ra.height); })[0];
      if (!svg) return '';
      const w = svg.getBoundingClientRect().width || 700, h = svg.getBoundingClientRect().height || 340;
      const clone = svg.cloneNode(true); clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      const url = URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(clone)], { type: 'image/svg+xml;charset=utf-8' }));
      return await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => { const c = document.createElement('canvas'); c.width = w * 2; c.height = h * 2; const g = c.getContext('2d'); g.fillStyle = '#fff'; g.fillRect(0, 0, c.width, c.height); g.drawImage(img, 0, 0, c.width, c.height); URL.revokeObjectURL(url); resolve(c.toDataURL('image/png')); };
        img.onerror = () => { URL.revokeObjectURL(url); resolve(''); };
        img.src = url;
      });
    } catch { return ''; }
  };
  const discussDataPoint = async (point) => {
    if (!startDiscussion || !point) return;
    const ctx = {};
    if (point._issue) ctx['⚠ Flagged'] = point._issue;
    [...(msg.result?.dimensions || []), ...(msg.result?.measures || [])].forEach(f => {
      if (point[f] !== undefined && point[f] !== null && !String(f).startsWith('_')) ctx[resolveLabel(allFields, f) || f] = point[f];
    });
    (msg.result?.calculations || []).forEach(c => { const n = c?.name; if (n && point[n] !== undefined && point[n] !== null) ctx[resolveLabel(allFields, n) || n] = point[n]; });
    if (!Object.keys(ctx).length) Object.entries(point).forEach(([k, v]) => { if (!String(k).startsWith('_') && v !== null && v !== undefined) ctx[resolveLabel(allFields, k) || k] = v; });
    const screenshot = await capturePointPng();
    startDiscussion(msg, ctx, screenshot);
  };
  const onChartContextMenu = (e) => {
    if (!startDiscussion || !lastHoverRef.current) return;
    e.preventDefault();
    discussDataPoint(lastHoverRef.current);
  };


  const requestCert = async (p) => {
    const key = p.name;
    setCertLoading(s => ({ ...s, [key]: true }));
    try {
      const res = await api.requestExploreCertification({
        model_id: selectedModel,
        measure_name: p.name,
        formula: p.formula || '',
      });
      setCertRequested(s => ({ ...s, [key]: true }));
      toast.success(res?.message || 'Certification requested');
    } catch (err) {
      toast.error(err.message || 'Could not request certification');
    } finally {
      setCertLoading(s => ({ ...s, [key]: false }));
    }
  };

  // One-tap certification straight from a warned/ad-hoc answer — prefilled with
  // the derived metric's name (parsed from the verified formula) and formula.
  const [answerCertDone, setAnswerCertDone] = useState(false);
  const [answerCertLoading, setAnswerCertLoading] = useState(false);
  const requestCertFromAnswer = async () => {
    const formula = msg.result?.trust_formula || '';
    const parsedName = (formula.split('=')[0] || '').trim();
    const measureName = parsedName
      || (msg.result?.ad_hoc_measures || [])[0]
      || (msg.result?.measures || [])[0]
      || 'Ad-hoc metric';
    setAnswerCertLoading(true);
    try {
      const res = await api.requestExploreCertification({
        model_id: selectedModel,
        measure_name: measureName,
        formula,
        business_definition: msg.result?.trust_explanation || '',
      });
      setAnswerCertDone(true);
      toast.success(res?.message || 'Certification requested');
    } catch (err) {
      toast.error(err.message || 'Could not request certification');
    } finally {
      setAnswerCertLoading(false);
    }
  };


  // Steward one-click certify straight from the provenance chip. On success the
  // measure flips to certified and — for a percentage-formatted measure — its
  // values immediately render as a percentage (unit override), matching how the
  // certified format is authoritative everywhere.
  const certifyNow = async (p) => {
    if (!p.measure_id) { toast.error('This value has no governed measure to certify'); return; }
    const reason = window.prompt(`Reason for certifying "${p.label || p.name}" (recorded with this version):`, '');
    if (reason == null || !reason.trim()) return;
    const key = p.name;
    setCertLoading(s => ({ ...s, [key]: true }));
    try {
      await api.certifyMeasure(p.measure_id, { reason: reason.trim() });
      setCertifiedLocal(s => ({ ...s, [key]: true }));
      // If the measure's format is a percentage, reflect it live.
      if (String(p.format || '').includes('%')) {
        setUnitOverrides(prev => ({ ...prev, [p.name]: 'percent_decimal' }));
      }
      toast.success(`${p.label || p.name} certified`);
    } catch (err) {
      toast.error(err.message || 'Could not certify measure');
    } finally {
      setCertLoading(s => ({ ...s, [key]: false }));
    }
  };

  const handleThumbsUp = async () => {
    setFeedbackState('up');
    const isAdminUser = user?.role === 'admin' || user?.role === 'super_admin';
    try {
      await api.exploreFeedback({
        session_id: sessionId, model_id: selectedModel,
        question: msg.question || msg.userQuestion || '', result: msg.result,
        vote: 'up',
      });
      // Thumbs-up on a SQL answer promotes it into the model's SQL Library so
      // the AI reuses the proven query for similar questions (admins only).
      if (isAdminUser && msg.result?.sql_mode && msg.result?.sql && !msg.result?.error) {
        try {
          await api.createSqlExample({ model_id: selectedModel, question: msg.question || msg.userQuestion || '', sql: msg.result.sql, source: 'saved' });
          toast.success('Saved to the SQL Library — the AI will reuse this for similar questions');
          return;
        } catch (e) { /* fall through to the generic thanks toast */ }
      }
      toast.success('Thanks! This helps improve AI accuracy');
    } catch (err) { toast.error(err.message); }
  };

  const [metricRequested, setMetricRequested] = useState(false);
  const requestMissingMetric = async () => {
    const metricName = msg.question || 'Requested metric';
    try {
      await api.requestExploreCertification({
        model_id: selectedModel, measure_name: metricName, formula: '',
        business_definition: `Requested via AI Explore — this model lacks the fields to compute "${metricName}".`,
        missing_concepts: Array.isArray(msg.result?.missing_inputs) ? msg.result.missing_inputs : [],
      });
      setMetricRequested(true);
      toast.success('Sent to stewards — they can add this metric');
    } catch (err) { toast.error(err.message || 'Could not send request'); }
  };

  // PII-blocked answer → open the Request Access popup (mirrors the
  // PII & Data Access → Request Access screen) with the query's model
  // pre-chosen and the flagged PII field(s) pre-selected.
  const [piiRequested, setPiiRequested] = useState(false);
  const [piiDialogOpen, setPiiDialogOpen] = useState(false);

  const handleThumbsDown = async () => {
    if (!rejectCode) { toast.error('Pick what was wrong'); return; }
    setFeedbackLoading(true);
    const originalQuestion = msg.question || msg.userQuestion || '';
    // Iterate on the latest revision when re-rejecting, otherwise the original.
    const baseResult = reaskBase || msg.result;
    try {
      const res = await api.exploreReject({
        session_id: sessionId, model_id: selectedModel,
        question: originalQuestion, result: baseResult,
        reason_code: rejectCode, detail: downReason.trim(),
      });
      setReaskBase(null);
      setFeedbackState('down');
      setShowDownForm(false);
      setShowRevSql(false);
      if (res?.routed_to === 'data_quality') {
        setRejectOutcome({ kind: 'dq', message: res.message, incident_id: res.incident_id });
        toast.success('Logged as a data-quality incident');
      } else {
        setRejectOutcome({ kind: 'revision', plan_diff: res?.plan_diff, result: res?.result,
                           message: res?.message, reason_label: res?.reason_label,
                           correction_id: res?.correction_id, accepted: false });
        // Render the revised answer as a real chart/table card so it can be
        // compared visually (not just a number/plan note).
        if (res?.result && onRevisedResult) {
          onRevisedResult(res.result, res.reason_label || downReason.trim() || rejectCode, msg.question || msg.userQuestion || '');
        }
        toast.success(res?.result ? 'Revised your answer based on the feedback' : (res?.message || 'Feedback saved'));
      }
    } catch (err) { toast.error(err.message || 'Could not submit feedback'); }
    finally { setFeedbackLoading(false); }
  };

  // Phase 3 inc-5: accept a revised answer so AI Explore learns the corrected
  // mapping for this question (personal + a tenant candidate a steward can promote).
  const handleAcceptCorrection = async () => {
    if (!rejectOutcome?.correction_id) return;
    try {
      const res = await api.acceptCorrection(rejectOutcome.correction_id, { result: rejectOutcome.result });
      setRejectOutcome((p) => ({ ...p, accepted: true }));
      toast.success(res?.message || 'Saved — AI Explore will remember this');
    } catch (err) { toast.error(err.message || 'Could not save'); }
  };

  // "Still wrong" on a revision — reopen the feedback form and iterate the next
  // rejection on the LATEST revised result (not the original) so corrections
  // build on each other.
  const handleRejectAgain = () => {
    setReaskBase(rejectOutcome?.result || msg.result);
    setRejectOutcome(null);
    setShowRevSql(false);
    setFeedbackState(null);
    setRejectCode('');
    setDownReason('');
    setShowDownForm(true);
  };

  // Close the thumbs-down loop: teach AI Explore the corrected answer for the
  // ORIGINAL question so it reuses it next time (and for close paraphrases).
  const handleTeach = async () => {
    const originalQuestion = msg.question || msg.userQuestion || '';
    if (!originalQuestion || !msg.result?.sql) { toast.error('Nothing to save'); return; }
    try {
      const res = await api.teachSqlExample({ session_id: sessionId, model_id: selectedModel, question: originalQuestion, sql: msg.result.sql, result: msg.result });
      if (res?.status === 'suggested') {
        setFixOutcome('suggested');
        toast.success('Sent to a steward to approve — thanks for flagging the right answer');
      } else {
        setFixOutcome('taught');
        toast.success('Saved — AI Explore will reuse this for similar questions');
      }
    } catch (err) { toast.error(err.message || 'Could not save'); }
  };

  // Unit-aware formatters — hooks must come BEFORE the user-role early return
  // so the hook order stays stable. The backend tags each measure with a
  // `_measure_units` map; we use the compact variant for chart axis ticks
  // and the regular one for tooltips, table cells, and the KPI tile so
  // '0.9325' becomes '93.3%' and '10700.66' becomes '$10.7K'.
  const measureUnits = msg?.result?._measure_units || null;
  // Format Override — let the user flip an ad-hoc ratio calculation between
  // percentage (109%) and plain-ratio (1.09x) display on the chart.
  const calcs = msg?.result?.calculations || [];
  const [unitOverrides, setUnitOverrides] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('exploreUnitOverrides') || '{}'); } catch { return {}; }
  });
  const [chartHelpOpen, setChartHelpOpen] = useState(false);
  useEffect(() => {
    try { sessionStorage.setItem('exploreUnitOverrides', JSON.stringify(unitOverrides)); } catch { /* ignore */ }
  }, [unitOverrides]);
  const effectiveUnits = useMemo(
    () => ((measureUnits || Object.keys(unitOverrides).length) ? { ...(measureUnits || {}), ...unitOverrides } : null),
    [measureUnits, unitOverrides]
  );
  const setCalcFormat = (name, mode, c) => setUnitOverrides(prev => ({
    ...prev,
    [name]: mode === 'ratio' ? 'ratio' : (c?.as_percent ? 'percent' : 'percent_decimal'),
  }));
  const formatValueWithUnits = useMemo(
    () => (effectiveUnits ? makeFormatValue(effectiveUnits, formatValue, { compactAxis: false }) : formatValue),
    [effectiveUnits, formatValue]
  );
  const formatAxis = useMemo(
    () => (effectiveUnits ? makeFormatValue(effectiveUnits, formatValue, { compactAxis: true }) : formatValue),
    [effectiveUnits, formatValue]
  );

  if (msg.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-2xl px-4 py-3 rounded-2xl bg-primary/10 border border-primary/20 text-sm text-foreground" data-testid={`msg-user-${idx}`}>
          {isSharedSession && msg.author && msg.author_id !== user?.user_id && (
            <p className="text-[9px] text-primary/60 font-medium mb-1">{msg.author}</p>
          )}
          {msg.content}
        </div>
      </div>
    );
  }

  // Auto-improve placeholder — shown while a low-quality answer is being
  // silently re-asked for a stronger one (Answer Quality Filter).
  if (msg._improving) {
    return (
      <div className="max-w-4xl" data-testid={`msg-improving-${idx}`}>
        <div className="glass-card rounded-2xl p-4 border border-amber-500/25 bg-amber-500/5 flex items-center gap-2.5">
          <Loader2 className="w-4 h-4 text-amber-400 animate-spin" />
          <span className="text-xs text-amber-800 dark:text-amber-200">Answer quality was low — improving it for you…</span>
        </div>
      </div>
    );
  }

  const hasViz = msg.liveData && msg.liveData.length > 0 && (msg.result?.chart_type || msg.result?.output_type === 'table' || msg.result?.dimensions?.length > 0);
  const isClarify = msg.result?.output_type === 'clarify';
  const isLowConfidence = !isClarify && msg.result?._low_confidence === true;
  const critiqueConfidence = msg.result?._confidence;
  const critiqueWeakness = msg.result?._weakness;
  const critiqueFollowup = msg.result?._suggested_followup;

  // KPI helper — render the headline number with its unit, falling back
  // to formatValue when no unit was assigned to the first measure.
  const kpiMeasure = msg.result?.measures?.[0];
  const kpiUnit = kpiMeasure ? (effectiveUnits?.[kpiMeasure] ?? null) : null;
  const renderKpi = (raw) => {
    if (kpiUnit) {
      const out = formatUnit(raw, kpiUnit);
      if (out !== null && out !== undefined) return out;
    }
    return formatValue(raw);
  };
  const kpiUnitLabel = kpiUnit ? unitAxisSuffix(kpiUnit) : null;

  // "Explain this number" — turn the router's route decision into a
  // human speed-win chip. Only renders when an aggregate was picked
  // AND we have measured duration for it.
  const routeDecision = msg.debugQuery?.route_decision;
  const durationMs = msg.debugQuery?.duration_ms || msg.result?.duration_ms;
  const speedWinChip = (() => {
    if (!routeDecision || !routeDecision.chosen_entity_id) return null;
    if (routeDecision.chosen_entity_id === routeDecision.canonical_entity_id) return null;
    if (!durationMs) return null;
    const considered = routeDecision.alternatives_considered_ok || [];
    const chosen = considered.find(c => c.entity_id === routeDecision.chosen_entity_id);
    const canonical = considered.find(c => c.entity_id === routeDecision.canonical_entity_id);
    if (!chosen || !canonical || !chosen.cost) return null;
    const speedup = canonical.cost / Math.max(chosen.cost, 1);
    const canonicalMs = Math.round(durationMs * speedup);
    return { chosenName: routeDecision.chosen_name, chosenMs: Math.round(durationMs), canonicalMs, speedup };
  })();

  // Route Explainer — always tell the user which physical table served the
  // answer (pre-aggregated summary vs. the transaction fact) and why. Renders
  // whenever an aggregate was in play (chosen or considered/rejected).
  const routeInfo = (() => {
    if (!routeDecision || !routeDecision.chosen_entity_id) return null;
    const isSummary = routeDecision.chosen_entity_id !== routeDecision.canonical_entity_id;
    const rejected = routeDecision.alternatives_rejected || [];
    const consideredOk = routeDecision.alternatives_considered_ok || [];
    const hadAggregate = isSummary || rejected.length > 0 || consideredOk.length > 1;
    if (!hadAggregate) return null;   // no aggregate defined → nothing to explain
    const why = isSummary
      ? `Served from the pre-aggregated summary "${routeDecision.chosen_name}"${routeDecision.chosen_table ? ` (${routeDecision.chosen_table})` : ''} — faster than scanning the transaction fact.${speedWinChip ? ` ~${speedWinChip.speedup.toFixed(1)}× less data.` : ''}`
      : `Served from the transaction fact "${routeDecision.chosen_table || routeDecision.chosen_name}". ` + (rejected.length ? `Summary skipped: ${rejected[0].reason}` : 'No compatible summary for this grain.');
    return {
      isSummary,
      label: isSummary ? `Summary: ${routeDecision.chosen_name}` : 'Transaction fact',
      why,
      speedup: speedWinChip?.speedup,
    };
  })();

  // ── Per-result view state (chart type, legend, colours, sort, top-N) ──
  // Lives on the message as `viz_state`; every change is deterministic and
  // client-side (persisted via onVizChange) — never an AI request.
  const vizState = msg.viz_state || {};
  const _labelsOn = vizState.dataLabels ?? (() => { try { return sessionStorage.getItem('exploreDataLabels') === '1'; } catch { return false; } })();
  // Auto Map: when a result is grouped by a SINGLE geographic dimension (state,
  // country, region, jurisdiction…) with a measure AND has no second breakdown,
  // default to a US/world map. A second breakdown (colour series) can't be shown
  // on a map, so the map is skipped in favour of the grouped chart. Switchable.
  const _dims0 = msg.result?.dimensions || [];
  const _geoAuto = !vizState.chartType && !vizState.outputType
    && _dims0.length === 1 && (msg.result?.measures?.length > 0) && !msg.result?.color_by
    && (GEO_DIM_RE.test(_dims0[0]) || GEO_DIM_RE.test(resolveLabel(allFields, _dims0[0]) || ''));
  const effectiveChartType = vizState.chartType || (_geoAuto ? 'geo_map' : msg.result?.chart_type);
  const forceTable = vizState.outputType === 'table';
  const forceChart = vizState.outputType === 'chart';
  // Sparkline "Trend" toggle (table view, single-dimension results). Fetches a
  // per-dimension time series so each row shows its metric's direction.
  const _chartActive = !!(effectiveChartType && (msg.result?.dimensions?.length > 0) && !forceTable && (forceChart || msg.result?.output_type !== 'table'));
  const _tableActive = !_chartActive && (forceTable || msg.result?.output_type === 'table' || (!effectiveChartType && (msg.result?.dimensions?.length > 0)));
  const trendEligible = _tableActive && !!msg.liveData?.length
    && (msg.result?.dimensions || []).length === 1
    && ((msg.result?.measures || []).length >= 1 || (msg.result?.calculations || []).length >= 1);
  const loadTrend = async () => {
    if (trendOn) { setTrendOn(false); return; }
    setTrendOn(true);
    if (trendSeries || trendUnavailable) return;
    setTrendLoading(true);
    try {
      const resp = await api.exploreTrend({
        model_id: selectedModel,
        dimension: (msg.result?.dimensions || [])[0],
        measures: msg.result?.measures || [],
        calculations: msg.result?.calculations || [],
        filters: Array.isArray(msg.result?.filters_applied) ? msg.result.filters_applied : [],
      });
      if (resp?.available && resp.series && Object.keys(resp.series).length) {
        setTrendSeries(resp.series);
        setTrendMeta({
          labels: (resp.periods || []).map(p => formatDateByGrain(p, resp.grain)),
          target: resp.target,
        });
      } else {
        setTrendUnavailable(true);
      }
    } catch {
      setTrendUnavailable(true);
    } finally {
      setTrendLoading(false);
    }
  };
  const vizConfig = {
    palette: vizState.palette || 'default',
    orientation: vizState.orientation || 'vertical',
    barLayout: vizState.barLayout,
    dataLabels: !!_labelsOn,
    stacked: !!vizState.stacked,
    showLabels: !!_labelsOn,
    donut: vizState.donut,
    geoMode: vizState.geoMode,
    basemap: vizState.basemap,
    geoScope: vizState.geoScope,
    showAxisTitles: vizState.showAxisTitles !== false,
    logScale: !!vizState.logScale,
    axisFromZero: !!vizState.axisFromZero,
    lineStyle: vizState.lineStyle,
  };
  const vizThemeColors = (vizState.colors && vizState.colors.length) ? vizState.colors : undefined;
  const vizShowLegend = vizState.showLegend !== false;
  const vizShowGrid = vizState.showGrid !== false;
  // Number display override ("in millions", "round to whole", "abbreviate").
  const _numFmt = vizState.numberFormat;
  const chartFormatAxis = (() => {
    if (!_numFmt || _numFmt === 'full') return formatAxis;
    return (val, key) => {
      if (typeof val !== 'number') return formatAxis(val, key);
      if (_numFmt === 'whole') return Math.round(val).toLocaleString();
      if (_numFmt === 'millions') return (val / 1e6).toLocaleString(undefined, { maximumFractionDigits: 2 }) + 'M';
      if (_numFmt === 'thousands') return (val / 1e3).toLocaleString(undefined, { maximumFractionDigits: 2 }) + 'K';
      if (_numFmt === 'abbreviate') {
        const a = Math.abs(val);
        if (a >= 1e9) return (val / 1e9).toFixed(1) + 'B';
        if (a >= 1e6) return (val / 1e6).toFixed(1) + 'M';
        if (a >= 1e3) return (val / 1e3).toFixed(1) + 'K';
        return String(Math.round(val));
      }
      return formatAxis(val, key);
    };
  })();
  const emitViz = (patch) => {
    if (patch && Object.prototype.hasOwnProperty.call(patch, 'dataLabels')) {
      try { sessionStorage.setItem('exploreDataLabels', patch.dataLabels ? '1' : '0'); } catch { /* ignore */ }
    }
    onVizChange && onVizChange(idx, patch);
  };
  // Date grains from the parsed query — used to render period values ("Nov
  // 2022" not "2022-11-01") and grain-aware axis labels ("Book Month").
  const dateGrains = msg.result?.date_grains || {};
  const dgKeys = Object.keys(dateGrains);
  const fieldOverrides = {};
  for (const [fld, grain] of Object.entries(dateGrains)) {
    fieldOverrides[fld] = { displayName: grainDimLabel(resolveLabel(allFields, fld), grain) };
  }
  const transformRows = (rows) => {
    if (!Array.isArray(rows) || !rows.length) return rows;
    const dims = msg.result?.dimensions || [];
    const primaryDim = (msg.result?.x_axis && dims.includes(msg.result.x_axis)) ? msg.result.x_axis : dims[0];
    const measure = (msg.result?.measures || [])[0];
    let out = rows;
    if (vizState.sort && (vizState.sort.by === 'measure' ? measure : primaryDim)) {
      const dir = vizState.sort.dir === 'asc' ? 1 : -1;
      out = [...rows].sort((a, b) => (vizState.sort.by === 'measure'
        ? ((a[measure] ?? 0) - (b[measure] ?? 0)) * dir
        : String(a[primaryDim] ?? '').localeCompare(String(b[primaryDim] ?? '')) * dir));
      if (primaryDim) out = out.map((r, i) => ({ ...r, ['_sort_' + primaryDim]: i }));
    }
    if (vizState.topN) out = out.slice(0, vizState.topN);
    // Render grained date values as human periods, keeping the raw ISO in a
    // companion `_sort_` column so the chart still orders chronologically.
    if (dgKeys.length) {
      out = out.map(r => {
        const nr = { ...r };
        for (const k of dgKeys) {
          if (nr[k] === null || nr[k] === undefined) continue;
          if (!('_sort_' + k in nr)) nr['_sort_' + k] = String(nr[k]);
          nr[k] = formatDateByGrain(nr[k], dateGrains[k]);
        }
        return nr;
      });
    }
    return out;
  };
  // Ratio guardrail (table only): grey out rows whose divide-denominator is
  // missing/zero, so a null/0 ratio there is clearly not a real number.
  const markRatioGuardrails = (rows) => {
    if (!Array.isArray(rows) || !rows.length) return rows;
    const ratioDens = (msg.result?.calculations || [])
      .filter(c => (c.operator === 'divide' || !c.operator) && c.denominator)
      .map(c => c.denominator);
    if (!ratioDens.length) return rows;
    return rows.map(r => (ratioDens.some(d => !r[d]) ? { ...r, _muted: true } : r));
  };
  // Anomaly callouts (table only): flag rows whose target-metric value is a
  // clear outlier vs the other rows (Tukey IQR fence), so anomalies stand out.
  // Needs enough rows to judge; skips muted (missing-denominator) rows.
  const markAnomalies = (rows) => {
    if (!Array.isArray(rows) || rows.length < 5) return rows;
    const calcs = msg.result?.calculations || [];
    const measures = msg.result?.measures || [];
    const target = (calcs[0] && calcs[0].name) || measures[0];
    if (!target) return rows;
    const vals = rows.filter(r => !r._muted).map(r => Number(r[target])).filter(v => Number.isFinite(v));
    if (vals.length < 5) return rows;
    const sorted = [...vals].sort((a, b) => a - b);
    const q = (p) => { const idx = (sorted.length - 1) * p; const lo = Math.floor(idx), hi = Math.ceil(idx); return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo); };
    const q1 = q(0.25), q3 = q(0.75), iqr = q3 - q1;
    if (iqr <= 0) return rows;
    const median = q(0.5);
    const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
    const mult = anomalyStrictness === 'strict' ? 3.0 : anomalyStrictness === 'more' ? 1.0 : 1.5;
    const loFence = q1 - mult * iqr, hiFence = q3 + mult * iqr;
    const dim = (msg.result?.dimensions || [])[0];
    const stats = { target, targetLabel: resolveLabel(allFields, target) || target, dim, dimLabel: dim ? (resolveLabel(allFields, dim) || dim) : '', q1, q3, iqr, median, mean, loFence, hiFence, mult };
    return rows.map(r => {
      if (r._muted) return r;
      const v = Number(r[target]);
      if (!Number.isFinite(v)) return r;
      if (v > hiFence) return { ...r, _anomaly: 'high', _anomalyStats: stats };
      if (v < loFence) return { ...r, _anomaly: 'low', _anomalyStats: stats };
      return r;
    });
  };
  const hasMultiSeries = !!(msg.result?.color_by) || (msg.result?.measures || []).length > 1;

  // Reference line (average of the primary measure, or a fixed target).
  let vizRefLineValue;
  let vizRefLineLabel;
  const _refLine = vizState.refLine;
  if (_refLine && _refLine.mode && _refLine.mode !== 'none') {
    const _measure = (msg.result?.measures || [])[0];
    if (_refLine.mode === 'avg' && _measure && Array.isArray(msg.liveData) && msg.liveData.length) {
      const _vals = msg.liveData.map(r => Number(r[_measure])).filter(v => !Number.isNaN(v));
      if (_vals.length) {
        vizRefLineValue = _vals.reduce((a, b) => a + b, 0) / _vals.length;
        vizRefLineLabel = `Avg ${formatAxis(vizRefLineValue)}`;
      }
    } else if (_refLine.mode === 'target' && typeof _refLine.value === 'number') {
      vizRefLineValue = _refLine.value;
      vizRefLineLabel = `Target ${formatAxis(_refLine.value)}`;
    }
  }

  // Toolbar icons — plain JSX (not a component) to avoid remounting on parent re-render
  const buildToolbar = (leading = null) => (
    <div className="absolute top-2 right-2 flex items-center gap-0.5 z-10 bg-background/60 backdrop-blur-sm rounded-lg px-0.5 py-0.5 border border-border/20">
      {leading}
      {trendEligible && (
        <button
          onClick={loadTrend}
          disabled={trendLoading}
          title={trendUnavailable ? 'No time trend available for this metric' : 'Show a per-row trend sparkline over time'}
          data-testid="trend-toggle"
          className={`px-1.5 py-0.5 mr-1 rounded text-[9px] font-medium transition-colors ${trendOn && trendSeries ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'} ${trendUnavailable ? 'opacity-50' : ''}`}
        >
          {trendLoading ? '…' : 'Trend'}
        </button>
      )}
      {_tableActive && !!msg.liveData?.length && (msg.liveData.length >= 5) && (msg.result?.dimensions || []).length === 1 && (
        <div className="inline-flex items-center gap-0.5 mr-1 rounded-md border border-border/40 p-0.5" title="How strict the outlier flag is" data-testid="anomaly-sensitivity">
          {[['strict', 'Strict'], ['balanced', 'Balanced'], ['more', 'More']].map(([id, label]) => (
            <button
              key={id}
              onClick={() => setAnomalyStrictness(id)}
              data-testid={`anomaly-sens-${id}`}
              className={`px-1.5 py-0.5 rounded text-[9px] font-medium transition-colors ${anomalyStrictness === id ? 'bg-amber-500/20 text-amber-500' : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'}`}
            >{label}</button>
          ))}
        </div>
      )}
      {calcs.filter(c => (c.operator || 'divide') === 'divide').map(c => {
        const isRatio = effectiveUnits?.[c.name] === 'ratio';
        return (
          <div key={c.name} className="flex items-center gap-1 mr-1" title={`Display "${c.name}" as percent or ratio`}>
            <span className="text-[9px] text-muted-foreground max-w-[90px] truncate hidden sm:inline">{c.name}</span>
            <div className="flex rounded-md overflow-hidden border border-border/40">
              <button
                onClick={() => setCalcFormat(c.name, 'percent', c)}
                className={`px-1.5 py-0.5 text-[9px] transition-colors ${!isRatio ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:bg-muted/40'}`}
                data-testid={`calc-fmt-percent-${c.name}`}
              >%</button>
              <button
                onClick={() => setCalcFormat(c.name, 'ratio', c)}
                className={`px-1.5 py-0.5 text-[9px] transition-colors ${isRatio ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:bg-muted/40'}`}
                data-testid={`calc-fmt-ratio-${c.name}`}
              >1.0x</button>
            </div>
          </div>
        );
      })}
      {routeInfo && (
        <span
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-medium border mr-0.5 ${routeInfo.isSummary ? 'bg-emerald-500/10 text-emerald-800 dark:text-emerald-300 border-emerald-500/30' : 'bg-sky-500/10 text-sky-800 dark:text-sky-300 border-sky-500/30'}`}
          title={routeInfo.why}
          data-testid="route-explainer-chip"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-2.5 h-2.5">
            {routeInfo.isSummary ? <polyline points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/> : <><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5v14a9 3 0 0 0 18 0V5"/></>}
          </svg>
          {routeInfo.label}{routeInfo.isSummary && routeInfo.speedup ? ` · ${routeInfo.speedup.toFixed(1)}× faster` : ''}
        </span>
      )}
      {msg.debugQuery?.drill_across && (() => {
        const da = msg.debugQuery.drill_across;
        const facts = (da.fact_labels && da.fact_labels.length ? da.fact_labels : da.facts) || [];
        const onDims = (da.dimension_labels && da.dimension_labels.length ? da.dimension_labels : da.dimensions) || [];
        if (facts.length < 2) return null;
        const factsText = facts.join(' + ');
        const onText = onDims.join(', ');
        return (
          <span
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-medium border mr-0.5 bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 border-indigo-500/20"
            title={`This answer combines measures from separate facts (${factsText}). Each fact was aggregated on its own and the results matched${onText ? ` on ${onText}` : ''} — not a row-level join, so there's no double counting.`}
            data-testid="cross-fact-chip"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-2.5 h-2.5">
              <circle cx="6" cy="12" r="3"/><circle cx="18" cy="12" r="3"/><path d="M9 12h6"/>
            </svg>
            Combined: {factsText}{onText ? ` · on ${onText}` : ''}
          </span>
        );
      })()}
      {msg.result?.date_interpretation?.was_defaulted && (
        <span
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-medium border mr-0.5 bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/20"
          title={msg.result.date_interpretation.hint}
          data-testid="date-default-chip"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-2.5 h-2.5">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
          {msg.result.date_interpretation.used_field_label || msg.result.date_interpretation.used_field}
        </span>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            title="Save this result"
            data-testid="viz-save-menu-btn"
            className="px-1.5 py-0.5 mr-0.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
          >
            <Save className="w-3 h-3" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52" data-testid="viz-save-menu">
          <DropdownMenuItem onClick={() => saveAsVisual?.(msg.result, vizState)} data-testid="save-menu-visual">
            <BarChart3 className="w-3.5 h-3.5 mr-2" /> Save as Visual
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => saveAsInsight?.(msg.result, vizState)} data-testid="save-menu-dashboard">
            <LayoutDashboard className="w-3.5 h-3.5 mr-2" /> Save as Dashboard/Report
          </DropdownMenuItem>
          {addToDashboard && (
            <DropdownMenuItem onClick={() => addToDashboard(msg.result, vizState)} data-testid="save-menu-add-dashboard">
              <FileText className="w-3.5 h-3.5 mr-2" /> Add to Dashboard/Report
            </DropdownMenuItem>
          )}
          {pinToKpi && can('ai_copilot:pin_kpi') && (msg.result?.measures?.length > 0) && (
            <DropdownMenuItem onClick={() => pinToKpi(msg.result, vizState)} data-testid="save-menu-pin-kpi">
              <Pin className="w-3.5 h-3.5 mr-2" /> Pin KPI
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <VizControls
        result={msg.result}
        vizState={vizState}
        onChange={emitViz}
        measures={msg.result?.measures || []}
        isChart={!forceTable && (forceChart || (effectiveChartType && msg.result?.output_type !== 'table'))}
        hasMultiSeries={hasMultiSeries}
        effectiveUnits={effectiveUnits}
        onUnitOverride={(name, unit) => setUnitOverrides(prev => ({ ...prev, [name]: unit }))}
      />
      {onChangeGrain && dgKeys.length > 0 && !isSharedSession && (
        <div className="inline-flex items-center gap-0.5 ml-0.5 rounded-md border border-border/50 bg-muted/30 p-0.5" data-testid="grain-picker" title="Change the date grain">
          {['day', 'week', 'month', 'quarter', 'year'].map(g => {
            const active = dateGrains[dgKeys[0]] === g;
            return (
              <button
                key={g}
                onClick={() => !active && onChangeGrain(msg, g)}
                data-testid={`grain-${g}`}
                className={`px-1.5 py-0.5 rounded text-[9px] font-medium capitalize transition-colors ${active ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'}`}
              >
                {g}
              </button>
            );
          })}
        </div>
      )}
      {can('ai_copilot:ai_response') && (
        <button onClick={() => setExploreQueryView({ ...msg.result, _debugQuery: msg.debugQuery })} className="p-1.5 rounded hover:bg-muted/40 text-muted-foreground hover:text-foreground transition-colors" title="View database query" data-testid="viz-view-query">
          <Code2 className="w-3.5 h-3.5" />
        </button>
      )}
      {onRerunQuery && !isSharedSession && !msg._viz_variant && (
        <button onClick={() => onRerunQuery(msg)} className="p-1.5 rounded hover:bg-muted/40 text-muted-foreground hover:text-foreground transition-colors" title="Refresh query (rerun with latest data)" data-testid="viz-rerun-query">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      )}
      {onReaskAI && !isSharedSession && !msg._viz_variant && (
        <button onClick={() => onReaskAI(msg, idx)} className="p-1.5 rounded hover:bg-muted/40 text-muted-foreground hover:text-foreground transition-colors" title="Re-ask AI this question" data-testid="viz-reask-ai">
          <Sparkles className="w-3.5 h-3.5" />
        </button>
      )}
      {onModelEdited && !isSharedSession && !msg._viz_variant && editableFields.length > 0 && (
        <button onClick={() => setQuickEditOpen(true)} className="p-1.5 rounded hover:bg-muted/40 text-muted-foreground hover:text-foreground transition-colors" title="Quick-edit a model field (display name, synonyms, description)" data-testid="viz-edit-field">
          <Pencil className="w-3.5 h-3.5" />
        </button>
      )}
      <ExportMenu data={msg.liveData} dims={msg.result?.dimensions || []} measures={msg.result?.measures || []} allFields={allFields} answer={msg.result?.answer} narrative={msg.result?.narrative} msgIdx={idx} debugQuery={msg.debugQuery} result={msg.result} vizState={vizState} />
    </div>
  );

  const vizToolbar = buildToolbar();

  return (
    <div className="max-w-4xl space-y-3" data-testid={`msg-result-${idx}`}>
      {/* Model changed → offer a one-click re-run to apply (Phase 1 re-sync) */}
      {needsResync && (
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-[11px] font-medium bg-amber-500/10 text-amber-800 dark:text-amber-300 border border-amber-500/40 w-fit" data-testid="model-resync-badge">
          <RefreshCw className="w-3 h-3" /> Model updated — re-run to apply
          <button onClick={() => { onRerunQuery && onRerunQuery(msg); }} className="underline underline-offset-2 hover:text-amber-700 dark:hover:text-amber-200" data-testid="model-resync-rerun">Re-run now</button>
        </div>
      )}
      {/* Swap confirm chip — shows how a "swap X with Y" was interpreted */}
      {onModelEdited && (
        <QuickEditFieldDrawer
          open={quickEditOpen}
          onOpenChange={setQuickEditOpen}
          modelId={modelId}
          fields={editableFields}
          onSaved={() => { onModelEdited && onModelEdited(); }}
        />
      )}
      {msg._swapNote && (
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-primary/10 text-primary border border-primary/20 w-fit" data-testid="swap-confirm-chip" title="How your swap request was interpreted">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3"><path d="M16 3h5v5"/><path d="M4 20 21 3"/><path d="M21 16v5h-5"/><path d="M15 15l6 6"/><path d="M4 4l5 5"/></svg>
          {msg._swapNote}
        </div>
      )}
      {/* Clarify card — model honestly can't answer */}
      {isClarify && (
        <div className="glass-card rounded-2xl p-5 border border-amber-500/30 bg-amber-500/5" data-testid="clarify-card">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
              <MessageSquare className="w-4 h-4 text-amber-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-wider text-amber-800 dark:text-amber-400 font-semibold mb-1">Honest answer</p>
              <p className="text-sm text-foreground leading-relaxed">{msg.result.answer}</p>
              {msg.result.narrative && <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{msg.result.narrative}</p>}
              {!(Array.isArray(msg.result.clarification_options) && msg.result.clarification_options.length > 0)
                && !msg.result.suggested_question
                && !(Array.isArray(msg.result.suggested_fields) && msg.result.suggested_fields.length > 0) && (
                <p className="text-xs text-muted-foreground/80 mt-2 leading-relaxed" data-testid="clarify-empty-hint">
                  Try naming the exact field or measure, or build the metric you need below.
                </p>
              )}
              {Array.isArray(msg.result.clarification_groups) && msg.result.clarification_groups.length > 0 && (
                <ClarifyGroups
                  groups={msg.result.clarification_groups}
                  baseQuestion={msg.result.base_question || ''}
                  variant={msg.result._variant || 'A'}
                  modelId={modelId}
                  onRun={(finalQ) => { setQuestion(finalQ); setTimeout(() => ask(finalQ), 100); }}
                />
              )}
              {Array.isArray(msg.result.clarification_options) && msg.result.clarification_options.length > 0 && (
                <div className="mt-3" data-testid="clarification-options">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground/70 font-semibold mb-1.5">Did you mean</p>
                  <div className="flex flex-wrap gap-1.5">
                    {msg.result.clarification_options.map((o, i) => (
                      <button
                        key={i}
                        onClick={() => { const q = o.question || o.label; try { api.recordClarifyPick({ kind: o.kind || 'unknown', label: o.label, variant: msg.result._variant || 'A', model_id: modelId, subject: o.subject, measure_name: o.measure_name, measure_label: o.measure_label, certified: o.certified }); } catch (e) { /* non-blocking */ } setQuestion(q); setTimeout(() => ask(q), 100); }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 text-xs text-amber-800 dark:text-amber-100 transition-colors"
                        data-testid={`clarification-option-${i}`}
                        title={o.reason || o.kind}
                      >
                        <ArrowRight className="w-3 h-3" /> {o.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {msg.result.suggested_question && (
                <button
                  onClick={() => { setQuestion(msg.result.suggested_question); setTimeout(() => ask(msg.result.suggested_question), 100); }}
                  className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/15 text-xs text-amber-800 dark:text-amber-200 transition-colors"
                  data-testid="clarify-suggested-question"
                >
                  <ArrowRight className="w-3 h-3" /> Try: {msg.result.suggested_question}
                </button>
              )}
              {onBuildMeasure && (
                <div className="mt-3 pt-3 border-t border-amber-500/20">
                  <p className="text-[10px] text-muted-foreground mb-1.5">Don't see the metric you need?</p>
                  <button
                    onClick={() => onBuildMeasure()}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-indigo-500/40 bg-indigo-500/10 hover:bg-indigo-500/20 text-xs text-indigo-700 dark:text-indigo-200 transition-colors"
                    data-testid="clarify-build-measure"
                  >
                    <Sparkles className="w-3 h-3" /> Build a new measure with AI
                  </button>
                </div>
              )}
              {Array.isArray(msg.result.suggested_fields) && msg.result.suggested_fields.length > 0 && (
                <div className="mt-3" data-testid="clarify-suggested-fields">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground/70 font-semibold mb-1.5">Related fields you can ask about</p>
                  <div className="flex flex-wrap gap-1.5">
                    {msg.result.suggested_fields.map((f, i) => (
                      <button
                        key={i}
                        onClick={() => { const q = `${resolveLabel(allFields, f.name) || f.label || f.name} by month`; setQuestion(q); setTimeout(() => ask(q), 100); }}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-cyan-500/40 bg-cyan-500/10 hover:bg-cyan-500/20 text-[11px] text-cyan-200 transition-colors"
                        data-testid={`suggested-field-${i}`}
                        title={f.kind}
                      >
                        {f.label || resolveLabel(allFields, f.name) || f.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Answer card — also shown for chart/table answers that carry measure
          provenance or certification info even when there's no text answer, so
          ad-hoc (uncertified) calculations are always surfaced. */}
      {!isClarify && (msg.result?.answer || (msg.result?._measure_provenance?.length > 0) || (msg.result?._certification_summary?.total > 0)) && (
        <div className="glass-card rounded-2xl p-5 border border-border/30">
          {msg._auto_improved && (
            <div className="mb-2 inline-flex items-center gap-1.5 text-[10px] text-emerald-800 dark:text-emerald-300 bg-emerald-500/10 border border-emerald-500/25 rounded-full px-2 py-0.5" data-testid="auto-improved-note">
              <Sparkles className="w-3 h-3" /> Improved automatically for higher quality
            </div>
          )}
          {msg.revisedFromFeedback && (
            <div className="mb-2 flex items-start gap-1.5 text-[11px] text-sky-700 dark:text-sky-200/90 bg-sky-500/10 border border-sky-500/25 rounded-lg px-2.5 py-1.5" data-testid="revised-from-feedback">
              <RefreshCw className="w-3 h-3 mt-0.5 flex-shrink-0 text-sky-400" />
              <span className="leading-relaxed"><b>Revised based on your feedback:</b> “{msg.revisedFromFeedback}”</span>
            </div>
          )}
          {msg.result?.pii_blocked && (
            <div className="mb-2 rounded-xl border border-rose-500/30 bg-rose-500/5 p-3" data-testid="pii-blocked-card">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-rose-400" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-rose-800 dark:text-rose-200">Answering this needs restricted (PII) data</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
                    This question requires the PII-classified field{(msg.result.pii_fields || []).length > 1 ? 's' : ''} {(msg.result.pii_fields || []).map(f => f.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())).join(', ')}, which your role can't access.
                  </p>
                  {piiRequested ? (
                    <span className="inline-block mt-2 text-[11px] text-emerald-600 dark:text-emerald-300" data-testid="pii-access-requested">Access request sent ✓</span>
                  ) : (
                    <Button size="sm" variant="outline" className="mt-2 h-6 px-2.5 text-[10px] rounded-full border-rose-500/40 text-rose-800 dark:text-rose-200 hover:bg-rose-500/10"
                      onClick={() => setPiiDialogOpen(true)} data-testid="request-pii-access-btn">
                      Request access
                    </Button>
                  )}
                </div>
              </div>
              <RequestPiiAccessDialog
                open={piiDialogOpen}
                onOpenChange={setPiiDialogOpen}
                modelId={msg.result?.model_id || selectedModel}
                preselectedFields={msg.result?.pii_fields || []}
                question={msg.question || msg.content || ''}
                onSubmitted={() => setPiiRequested(true)}
              />
            </div>
          )}
          {msg.result?.cannot_calculate && (
            <div className="mb-2 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3" data-testid="cannot-calculate-card">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-400" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-amber-800 dark:text-amber-200">I can't calculate that from this model</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">The data needed isn't available here, and I won't substitute a different metric. A steward can add the required fields or a certified measure.</p>
                  {Array.isArray(msg.result?.missing_inputs) && msg.result.missing_inputs.length > 0 && (
                    <div className="mt-2" data-testid="missing-inputs">
                      <p className="text-[10px] uppercase tracking-wider text-amber-700/80 dark:text-amber-300/80 mb-1">Inputs needed for this calculation</p>
                      <div className="flex flex-wrap gap-1.5">
                        {msg.result.missing_inputs.map((f) => (
                          <span key={f} className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] bg-amber-500/15 text-amber-800 dark:text-amber-200 border border-amber-500/30" data-testid={`missing-input-${f}`}>{f}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {metricRequested ? (
                    <span className="inline-block mt-2 text-[11px] text-emerald-700 dark:text-emerald-300" data-testid="metric-requested">Requested from stewards ✓</span>
                  ) : (
                    <Button size="sm" variant="outline" className="mt-2 h-6 px-2.5 text-[10px] rounded-full border-amber-500/40 text-amber-800 dark:text-amber-200 hover:bg-amber-500/10"
                      onClick={requestMissingMetric} data-testid="request-metric-btn">
                      Request this metric
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
          {msg.result?.interpreted_measure && (() => {
            const im = msg.result.interpreted_measure;
            const cert = im.certified ? ' · certified' : ' · ad-hoc';
            const label = <><b>{im.measure_label}</b>{cert}</>;
            let lead;
            if (im.source === 'remembered') lead = <>Using your usual measure for <b>{im.subject}</b>: {label}</>;
            else if (im.source === 'pinned') lead = <>Using your organization's default for <b>{im.subject}</b>: {label}</>;
            else lead = <>Interpreted as ranking by {label}</>;
            return (
            <div className="mb-2" data-testid="interpreted-measure-banner" data-source={im.source}>
              <div className="flex items-start gap-1.5 text-[11px] text-indigo-800 dark:text-indigo-200 bg-indigo-500/10 border border-indigo-500/40 rounded-lg px-2.5 py-1.5">
                <History className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-indigo-600 dark:text-indigo-400" />
                <span className="leading-relaxed">
                  {lead}{' · '}
                  <button className="underline underline-offset-2 hover:text-indigo-600 dark:hover:text-indigo-300 font-medium" onClick={() => setShowRemChange(v => !v)} data-testid="interpreted-measure-change">change</button>
                </span>
              </div>
              {showRemChange && Array.isArray(im.options) && (
                <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="interpreted-measure-options">
                  {im.options.map((o, i) => (
                    <button
                      key={i}
                      onClick={() => { const q = o.question || o.label; try { api.recordClarifyPick({ kind: o.kind || 'unknown', label: o.label, model_id: modelId, subject: o.subject, measure_name: o.measure_name, measure_label: o.measure_label, certified: o.certified }); } catch (e) { /* non-blocking */ } setShowRemChange(false); setQuestion(q); setTimeout(() => ask(q), 100); }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-indigo-500/40 bg-indigo-500/10 hover:bg-indigo-500/20 text-xs text-indigo-800 dark:text-indigo-100 transition-colors"
                      data-testid={`interpreted-measure-option-${i}`}
                      title={o.reason || ''}
                    >
                      <ArrowRight className="w-3 h-3" /> {o.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            );
          })()}
          {msg.result?.trust_warning && (
            <div className="mb-2 flex items-start gap-1.5 text-[11px] text-red-800 dark:text-red-200 bg-red-500/10 border border-red-500/40 rounded-lg px-2.5 py-1.5" data-testid="trust-warning">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-red-700 dark:text-red-400" />
              <span className="leading-relaxed"><b>Metric check:</b> {msg.result.trust_warning}</span>
            </div>
          )}
          {msg.result?.answered_by && (() => {
            const ab = msg.result.answered_by;
            const cfg = {
              'SQL Library': { cls: 'text-sky-800 dark:text-sky-300 bg-sky-500/10 border-sky-500/40', icon: <BookOpen className="w-3 h-3" />, tip: 'Answered from a saved, verified query in this model\'s SQL Library.' },
              'Certified': { cls: 'text-emerald-800 dark:text-emerald-300 bg-emerald-500/10 border-emerald-500/40', icon: <ShieldCheck className="w-3 h-3" />, tip: 'Backed by a governed, certified measure.' },
              'Generated': { cls: 'text-violet-800 dark:text-violet-300 bg-violet-500/10 border-violet-500/40', icon: <Code2 className="w-3 h-3" />, tip: 'Answered with SQL the AI generated for this question.' },
              'Governed': { cls: 'text-slate-700 dark:text-slate-300 bg-slate-500/10 border-slate-500/30', icon: <ShieldCheck className="w-3 h-3" />, tip: 'Answered from the governed semantic model.' },
              'Forecast': { cls: 'text-indigo-800 dark:text-indigo-300 bg-indigo-500/10 border-indigo-500/40', icon: <TrendingUp className="w-3 h-3" />, tip: 'A statistical forecast projection.' },
            }[ab];
            if (!cfg) return null;
            return (
              <div className="mb-2">
                <span className={`inline-flex items-center gap-1.5 text-[10px] border rounded-full px-2 py-0.5 font-medium ${cfg.cls}`}
                  data-testid="answered-by-badge" title={cfg.tip}>
                  {cfg.icon} Answered by: {ab}
                </span>
              </div>
            );
          })()}
          {msg.result?.trust_status && (() => {
            const st = msg.result.trust_status;
            const cfg = {
              certified: { cls: 'text-emerald-800 dark:text-emerald-300 bg-emerald-500/10 border-emerald-500/40', icon: <ShieldCheck className="w-3 h-3" /> },
              provisional: { cls: 'text-sky-800 dark:text-sky-300 bg-sky-500/10 border-sky-500/40', icon: <ShieldCheck className="w-3 h-3" /> },
              ad_hoc: { cls: 'text-amber-800 dark:text-amber-300 bg-amber-500/10 border-amber-500/40', icon: <Sigma className="w-3 h-3" /> },
              mixed: { cls: 'text-amber-800 dark:text-amber-200 bg-amber-500/10 border-amber-500/40', icon: <Sigma className="w-3 h-3" /> },
            }[st] || null;
            if (!cfg) return null;
            return (
              <div className="mb-2 flex items-center gap-2 flex-wrap">
                <div className={`inline-flex items-center gap-1.5 text-[10px] border rounded-full px-2 py-0.5 font-medium ${cfg.cls}`}
                  data-testid="trust-status-badge" title={msg.result.trust_explanation || ''}>
                  {cfg.icon} {msg.result.trust_label}
                  {st !== 'certified' && msg.result.trust_formula
                    ? <span className="opacity-90 font-normal normal-case">— {msg.result.trust_formula}</span> : null}
                </div>
                {st !== 'certified' && msg.result.trust_formula && selectedModel && (
                  answerCertDone ? (
                    <span className="text-[10px] font-medium text-emerald-700 dark:text-emerald-300" data-testid="answer-cert-requested">Certification requested ✓</span>
                  ) : (
                    <Button size="sm" variant="outline"
                      className="h-5 px-2 text-[9px] rounded-full border-emerald-500/50 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/10"
                      disabled={answerCertLoading}
                      onClick={requestCertFromAnswer}
                      data-testid="request-cert-from-answer"
                      title="Submit this metric and its verified formula to the certification review queue">
                      {answerCertLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Request certification'}
                    </Button>
                  )
                )}
              </div>
            );
          })()}
          {msg.result?.intent && msg.result.intent.intent && msg.result.intent.intent !== 'descriptive' && (() => {
            const it = msg.result.intent;
            const ho = msg.result.intent_handoff;
            const base = 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] border';
            const tone = {
              diagnostic: 'text-violet-700 dark:text-violet-300 bg-violet-500/10 border-violet-500/30',
              comparative: 'text-blue-700 dark:text-blue-300 bg-blue-500/10 border-blue-500/30',
              predictive: 'text-emerald-700 dark:text-emerald-300 bg-emerald-500/10 border-emerald-500/30',
              prescriptive: 'text-amber-700 dark:text-amber-300 bg-amber-500/10 border-amber-500/30',
            }[it.intent] || 'bg-muted/30 border-border/40 text-muted-foreground';
            const sigTip = (it.signals || []).length ? `Detected from: ${(it.signals || []).join(', ')}` : it.label;
            return (
              <div className="mb-2 flex items-center gap-1.5 flex-wrap" data-testid="answer-intent">
                {msg.result?.investigation_carryover?.subject && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] border border-border/40 bg-muted/30 text-muted-foreground" title={`You asked: "${msg.result.investigation_carryover.from_question}" — continuing your investigation of ${msg.result.investigation_carryover.subject}`} data-testid="investigation-carryover">
                    <History className="w-3 h-3" /> Continuing: {msg.result.investigation_carryover.subject}
                  </span>
                )}
                {msg.result?.investigation_context?.summary && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] border border-violet-500/30 bg-violet-500/10 text-violet-700 dark:text-violet-300" title="The active investigation this answer belongs to" data-testid="investigation-context-chip">
                    <Compass className="w-3 h-3" /> Investigation: {msg.result.investigation_context.summary}
                    {typeof resetInvestigation === 'function' && (
                      <button onClick={(e) => { e.stopPropagation(); resetInvestigation(); }} className="ml-1 hover:opacity-70" title="Reset investigation — start fresh" data-testid="investigation-reset-btn">✕</button>
                    )}
                  </span>
                )}
                <span className={`${base} ${tone}`} title={sigTip} data-testid={`intent-${it.intent}`}>
                  <Compass className="w-3 h-3" /> {it.label}
                </span>
                {ho?.type === 'diagnostic' && typeof runInvestigate === 'function' && (
                  <button
                    onClick={() => runInvestigate(`Why did ${msg.result?.measures?.[0] ? resolveLabel(allFields, msg.result.measures[0]) : 'this metric'} change?`, msg.result?.measures?.[0])}
                    disabled={investigateLoading}
                    className={`${base} ${tone} hover:opacity-80 cursor-pointer`}
                    title="Run the governed diagnostic engine — quantified drivers, tested & ruled-out, every number validated"
                    data-testid="intent-handoff-diagnostic">
                    {investigateLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />} {ho.label}
                  </button>
                )}
                {ho?.type === 'prescriptive' && (
                  <span className={`${base} ${tone}`} title={ho.note} data-testid="intent-handoff-prescriptive">
                    <Lightbulb className="w-3 h-3" /> {ho.label}
                  </span>
                )}
              </div>
            );
          })()}
          {msg.result?.answer_contract && (() => {
            const ac = msg.result.answer_contract;
            const chip = 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] border bg-muted/30 border-border/40 text-muted-foreground';
            const lin = ac.lineage || {};
            const linTip = [lin.model_id ? `Model: ${lin.model_id}` : '', ...(lin.measures || []).map(m => `${m.label || m.name}${m.certification ? ` (${m.certification}${m.version ? ` v${m.version}` : ''})` : ''}`)].filter(Boolean).join('\n');
            const repro = ac.reproducibility || {};
            const reproTip = `Plan ${repro.plan_id || '?'} · exec ${repro.exec_id || '?'}\nEngine: ${repro.engine || '?'} · ${repro.compiler_version || ''}\nModel: ${repro.model_id || '?'}${repro.model_version ? ` v${repro.model_version}` : ''}\nData through: ${repro.data_through || '?'}\nRun: ${repro.executed_at || '?'}\n(click for full record)`;
            const dq = ac.data_quality || {};
            return (
              <div className="mb-2 flex items-center gap-1.5 flex-wrap" data-testid="answer-contract">
                {ac.trust?.status === 'provisional' && (
                  <span className={`${chip} text-sky-700 dark:text-sky-300 bg-sky-500/10 border-sky-500/30`} title={(ac.trust.reasons || []).join(' ')} data-testid="contract-provisional">
                    <ShieldCheck className="w-3 h-3" /> Provisional
                  </span>
                )}
                {dq.low_reliability && (
                  <span className={`${chip} text-amber-700 dark:text-amber-300 bg-amber-500/10 border-amber-500/30`} title={dq.reliability_reason || 'Backed by few records'} data-testid="contract-low-reliability">
                    <AlertTriangle className="w-3 h-3" /> Low reliability
                  </span>
                )}
                {dq.suppressed_cells > 0 && (
                  <span className={`${chip} text-amber-700 dark:text-amber-300 bg-amber-500/10 border-amber-500/30`} title={`Cells with fewer than ${dq.min_cell_population} underlying records are hidden`} data-testid="contract-suppressed">
                    <EyeOff className="w-3 h-3" /> {dq.suppressed_cells} hidden
                  </span>
                )}
                {dq.under_investigation && (
                  <span className={`${chip} text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30`}
                    title={(dq.incidents || []).map(i => `${(i.severity || 'medium').toUpperCase()}: ${i.detail || i.incident_id}`).join('\n') || 'A data-quality incident is open against data used in this answer'}
                    data-testid="contract-dq">
                    <AlertTriangle className="w-3 h-3" /> Data under review
                  </span>
                )}
                {ac.as_of && (
                  <span className={chip} title="The as-of date this answer was measured against" data-testid="contract-asof">
                    <Clock className="w-3 h-3" /> As of {ac.as_of}
                  </span>
                )}
                {ac.freshness?.data_through && (
                  <span className={chip} title="The data in this answer is current through this date" data-testid="contract-freshness">
                    <Clock className="w-3 h-3" /> Data through {ac.freshness.data_through}
                  </span>
                )}
                {ac.comparison_basis && (
                  <span className={chip} title="Comparison basis for this answer" data-testid="contract-comparison">
                    <GitCompareArrows className="w-3 h-3" /> {ac.comparison_basis}
                  </span>
                )}
                {ac.applicability?.checked?.length > 0 && (
                  <span
                    className={ac.applicability.in_contract ? chip : `${chip} text-amber-700 dark:text-amber-300 bg-amber-500/10 border-amber-500/30`}
                    title={ac.applicability.in_contract
                      ? "Used within the certified measure's validated scope"
                      : [...(ac.applicability.violations || []), ...(ac.applicability.needs_data || [])].join(' ')}
                    data-testid="contract-applicability">
                    <ScrollText className="w-3 h-3" /> {ac.applicability.in_contract ? 'In contract' : 'Out of contract'}
                  </span>
                )}
                {(lin.measures || []).length > 0 && (
                  <span className={chip} title={linTip} data-testid="contract-lineage">
                    <Waypoints className="w-3 h-3" /> Lineage ({(lin.measures || []).length})
                  </span>
                )}
                {msg.result?.resolution && (
                  <span
                    className={chip}
                    title={`${msg.result.resolution.description}${msg.result.resolution.measure ? ` — ${msg.result.resolution.measure}` : ''}`}
                    data-testid="contract-resolution"
                    data-rung={msg.result.resolution.rung}>
                    <Layers className="w-3 h-3" /> Resolved: {msg.result.resolution.label}
                  </span>
                )}
                {repro.plan_id && (
                  <button type="button" onClick={() => setShowRepro(v => !v)}
                    className={`${chip} font-mono hover:bg-muted/60 transition-colors cursor-pointer`}
                    title={reproTip} data-testid="contract-repro">
                    <Fingerprint className="w-3 h-3" /> {repro.plan_id}
                  </button>
                )}
                <AnswerPinButton result={msg.result} chip={chip} />
                {showRepro && repro.plan_id && (
                  <div className="basis-full mt-1 rounded-lg border border-border/40 bg-muted/20 p-2.5 text-[10px] space-y-0.5" data-testid="repro-detail">
                    <p className="uppercase tracking-wide text-muted-foreground mb-1 font-medium">Reproducibility record</p>
                    <div className="grid sm:grid-cols-2 gap-x-4 gap-y-0.5 font-mono">
                      <span>plan: <b>{repro.plan_id}</b></span>
                      <span>exec: <b>{repro.exec_id || '—'}</b></span>
                      <span>engine: <b>{repro.engine || '—'}</b></span>
                      <span>compiler: <b>{repro.compiler_version || '—'}</b></span>
                      <span>model: <b>{repro.model_id || '—'}{repro.model_version ? ` v${repro.model_version}` : ''}</b></span>
                      <span>data through: <b>{repro.data_through || '—'}</b></span>
                      <span>trust: <b>{repro.trust_state || '—'}</b></span>
                      <span>run at: <b>{repro.executed_at ? new Date(repro.executed_at).toLocaleString() : '—'}</b></span>
                    </div>
                    {(repro.metric_versions || []).length > 0 && (
                      <p className="pt-1">metrics: {(repro.metric_versions).map(m => `${m.name}${m.version ? ` v${m.version}` : ''}${m.certification ? ` (${m.certification})` : ''}`).join(' · ')}</p>
                    )}
                    {repro.auth_scope && (
                      <p>scope: {repro.auth_scope.role || '—'} · {repro.auth_scope.user_id || '—'}</p>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
          {msg.result?.risk_signal && (() => {
            const rs = msg.result.risk_signal;
            const alert = rs.status === 'alert';
            const box = alert
              ? 'border-rose-500/40 bg-rose-500/5 text-rose-800 dark:text-rose-200'
              : 'border-amber-500/40 bg-amber-500/5 text-amber-800 dark:text-amber-200';
            return (
              <div
                className={`mb-2 p-2 rounded-lg border text-[11px] cursor-pointer hover:bg-muted/20 transition-colors ${box}`}
                onClick={() => navigate(`/admin/risk-signals?model_id=${encodeURIComponent(modelId || '')}`)}
                title="Open the Early-Warning Risk Signals page"
                data-testid="explore-risk-signal">
                <div className="flex items-center gap-1.5 font-medium">
                  <Siren className="w-3.5 h-3.5" /> Related risk signal — {rs.title}
                  <span className="ml-auto uppercase text-[9px] tracking-wide opacity-80">{alert ? 'Alert' : 'Warning'}</span>
                </div>
                <div className="mt-1 text-muted-foreground" data-testid="explore-risk-headline">{rs.headline}</div>
                {rs.generated_at && (
                  <div className="mt-0.5 text-[10px] text-muted-foreground/80">As of {new Date(rs.generated_at).toLocaleDateString()} · from your governed early-warning signals</div>
                )}
              </div>
            );
          })()}
          {msg.result?.diagnostic && <DiagnosticCard result={msg.result} />}
          {msg.result?.comparative && (() => {
            const c = msg.result.comparative;
            const fmtN = (n) => {
              if (n === null || n === undefined) return '—';
              const a = Math.abs(n);
              if (a >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
              if (a >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
              if (a >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
              return `${Math.round(n).toLocaleString()}`;
            };
            if (c.status !== 'ok') {
              return (
                <div className="mb-2 p-2 rounded-lg border border-blue-500/30 bg-blue-500/5 text-[11px] text-blue-800 dark:text-blue-200" data-testid="comparative-card">
                  <div className="flex items-center gap-1.5 font-medium"><GitCompareArrows className="w-3.5 h-3.5" /> {c.basis_label}</div>
                  <div className="mt-1 text-muted-foreground" data-testid="comparative-note">{c.note}</div>
                </div>
              );
            }
            const up = (c.pct_change ?? 0) > 0;
            return (
              <div className="mb-2 p-2.5 rounded-lg border border-blue-500/30 bg-blue-500/5" data-testid="comparative-card">
                <div className="flex items-center gap-1.5 text-[11px] font-medium text-blue-800 dark:text-blue-200 mb-1.5">
                  <GitCompareArrows className="w-3.5 h-3.5" /> {c.basis_label}
                </div>
                <div className="flex items-center gap-3 flex-wrap text-[11px]">
                  <div><span className="text-muted-foreground">{c.current?.period}</span> <b>{fmtN(c.current?.value)}</b></div>
                  {c.comparison && (
                    <div className="text-muted-foreground">{c.comparison.period} {fmtN(c.comparison.value)}</div>
                  )}
                  {c.pct_change !== null && c.pct_change !== undefined && (
                    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] border ${up ? 'text-emerald-700 dark:text-emerald-300 bg-emerald-500/10 border-emerald-500/30' : 'text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30'}`} data-testid="comparative-delta">
                      {up ? '▲' : '▼'} {Math.abs(c.pct_change).toFixed(1)}%
                    </span>
                  )}
                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] border ${c.within_normal ? 'text-muted-foreground bg-muted/30 border-border/40' : 'text-amber-700 dark:text-amber-300 bg-amber-500/10 border-amber-500/30'}`} data-testid="comparative-normal">
                    {c.within_normal ? 'Within normal range' : 'Outside normal range'} ({fmtN(c.normal_range?.low)}–{fmtN(c.normal_range?.high)})
                  </span>
                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] border ${c.significant ? 'text-violet-700 dark:text-violet-300 bg-violet-500/10 border-violet-500/30' : 'text-muted-foreground bg-muted/30 border-border/40'}`} data-testid="comparative-significance" title={`z = ${c.z_score}`}>
                    {c.significant ? 'Significant change' : 'Not significant'}
                  </span>
                </div>
              </div>
            );
          })()}
          {msg.result?.trust_verification && !msg.result?.trust_warning && (
            <div className="mb-2 flex items-start gap-1.5 text-[10px] text-amber-800 dark:text-amber-200" data-testid="trust-verification">
              <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0 text-amber-700 dark:text-amber-400" />
              <span className="leading-relaxed">{msg.result.trust_verification}</span>
            </div>
          )}
          {isAdHocMetric && !msg.result?.trust_status && (
            <div className="mb-2 inline-flex items-center gap-1.5 text-[10px] font-medium text-amber-800 dark:text-amber-300 bg-amber-500/10 border border-amber-500/40 rounded-full px-2 py-0.5" data-testid="ad-hoc-calc-badge" title={adHocFormula ? `Ad-hoc calculation: ${adHocFormula}` : "This metric isn't a defined measure in the model — the AI calculated it on the fly."}>
              <Sigma className="w-3 h-3" /> Ad-hoc calculation{adHocFormula ? <span className="opacity-90 font-normal normal-case">— {adHocFormula}</span> : null}
            </div>
          )}
          {msg.result.answer && <p className="text-sm text-foreground leading-relaxed">{msg.result.answer}</p>}
          {(msg.result.answer || msg.result.narrative) && (
            <div className="flex items-start gap-2 mt-2">
              {msg.result.narrative
                ? <p className="text-xs text-muted-foreground leading-relaxed flex-1">{msg.result.narrative}</p>
                : <span className="flex-1" />}
              <button
                onClick={() => { navigator.clipboard.writeText([msg.result?.answer, msg.result?.narrative].filter(Boolean).join('\n\n')); toast.success('Copied'); }}
                title="Copy this answer to your clipboard"
                data-testid="copy-narrative-btn"
                className="p-1 rounded-md text-muted-foreground/70 hover:text-primary hover:bg-primary/10 transition-colors flex-shrink-0"
              >
                <Copy className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          {msg.result?.ad_hoc_proposal && (
            <AdHocProposalCard
              proposal={msg.result.ad_hoc_proposal}
              modelId={selectedModel}
              canCertify={canCertify || can('measures:certify')}
              onAsk={(q) => { if (typeof setQuestion === 'function') setQuestion(q); if (typeof ask === 'function') setTimeout(() => ask(q), 60); }}
            />
          )}
          {msg.result.status_chips?.values?.length > 0 && (
            <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid="explore-status-chips">
              <span className="text-[10px] text-muted-foreground/80 uppercase tracking-wider">{msg.result.status_chips.label}</span>
              {msg.result.status_chips.values.map((c, i) => (
                <span key={i} data-testid={`status-chip-${i}`}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] border ${c.is_current ? 'bg-primary/15 border-primary/40 text-primary font-semibold' : 'bg-muted/30 border-border/40 text-muted-foreground'}`}>
                  {String(c.value)} <span className="font-semibold">{c.count.toLocaleString()}</span>
                </span>
              ))}
            </div>
          )}
          {msg.result._measure_windows && Object.keys(msg.result._measure_windows).length > 0 && (() => {
            const WIN = {
              ITD: { label: 'ITD', title: 'Inception-to-date: cumulative through the as-of date', cls: 'border-violet-500/40 text-violet-700 dark:text-violet-300 bg-violet-500/10' },
              YTD: { label: 'YTD', title: 'Year-to-date: from the start of the year through the as-of date', cls: 'border-sky-500/40 text-sky-700 dark:text-sky-300 bg-sky-500/10' },
              QTD: { label: 'QTD', title: 'Quarter-to-date: from the start of the quarter through the as-of date', cls: 'border-teal-500/40 text-teal-700 dark:text-teal-300 bg-teal-500/10' },
              MTD: { label: 'MTD', title: 'Month-to-date: within the as-of month only', cls: 'border-amber-500/40 text-amber-800 dark:text-amber-200 bg-amber-500/10' },
              WTD: { label: 'WTD', title: 'Week-to-date: from the start of the week through the as-of date', cls: 'border-rose-500/40 text-rose-700 dark:text-rose-300 bg-rose-500/10' },
              LTD: { label: 'LTD', title: 'Life-to-date: all data through the as-of date', cls: 'border-indigo-500/40 text-indigo-700 dark:text-indigo-300 bg-indigo-500/10' },
            };
            return (
              <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid="explore-window-chips">
                <span className="text-[10px] text-muted-foreground/80 uppercase tracking-wider">Time window</span>
                {Object.entries(msg.result._measure_windows).map(([name, win], i) => {
                  const w = WIN[String(win).toUpperCase()] || { label: win, title: `${win} time window`, cls: 'border-border/40 text-muted-foreground bg-muted/30' };
                  return (
                    <span key={i} title={`${name} — ${w.title}`} data-testid={`measure-window-chip-${i}`}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] border ${w.cls}`}>
                      <span className="truncate max-w-[140px]">{name}</span>
                      <span className="font-semibold">{w.label}</span>
                    </span>
                  );
                })}
              </div>
            );
          })()}
          {(!msg.result._measure_provenance || msg.result._measure_provenance.length === 0) && msg.result._provenance_summary && !(msg.result._provenance_summary.certified > 0) && (
            <div className="mt-3 pt-3 border-t border-amber-500/25 flex items-start gap-2 flex-wrap" data-testid="measure-provenance-adhoc">
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-amber-500/40 text-amber-800 dark:text-amber-200 bg-amber-500/10 text-[10px] font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />Not certified
              </span>
              <span className="text-[10px] text-amber-800 dark:text-amber-200">{msg.result.ad_hoc
                ? 'These numbers are an ad-hoc calculation — not from a governed measure. Define a certified measure for consistent, governed results.'
                : 'This answer isn\u2019t backed by a certified measure — the values come from raw fields, not a governed metric. Define a certified measure for consistent, governed results.'}</span>
              {onBuildMeasure && (
                <Button size="sm" variant="outline" className="h-5 px-2 text-[9px] rounded-full border-primary/40 text-primary hover:bg-primary/10"
                  onClick={onBuildMeasure} data-testid="adhoc-define-measure">Define a measure</Button>
              )}
            </div>
          )}
          {(msg.result._measure_provenance && msg.result._measure_provenance.length > 0) ? (() => {
            const prov = msg.result._measure_provenance;
            const KIND = {
              certified: { label: 'Certified', cls: 'border-emerald-500/40 text-emerald-700 dark:text-emerald-300 bg-emerald-500/10', dot: 'bg-emerald-400' },
              uncertified: { label: 'Uncertified', cls: 'border-amber-500/40 text-amber-800 dark:text-amber-200 bg-amber-500/10', dot: 'bg-amber-400' },
              ai_calculation: { label: 'AI calculation', cls: 'border-sky-500/40 text-sky-700 dark:text-sky-200 bg-sky-500/10', dot: 'bg-sky-400' },
              field: { label: 'Field total', cls: 'border-border/50 text-muted-foreground bg-muted/20', dot: 'bg-muted-foreground/60' },
            };
            // Clean = certified/field totals or measures the user just certified.
            // Attention = uncertified / ad-hoc AI calcs that a user can request or certify.
            const isClean = (p) => certifiedLocal[p.name] || p.kind === 'certified' || p.kind === 'field';
            const cleanItems = prov.filter(isClean);
            const attentionItems = prov.filter(p => !isClean(p));
            const allClean = attentionItems.length === 0;
            return (
              <div className={`mt-3 pt-3 border-t ${allClean ? 'border-emerald-500/20' : 'border-amber-500/25'}`} data-testid="measure-provenance">
                {/* Certified measures are summarised compactly (expand to see them) — only
                    uncertified/draft measures are listed individually so they don't hog space. */}
                {cleanItems.length > 0 && (
                  <div>
                    <button type="button" onClick={() => setProvExpanded(v => !v)}
                      className="flex items-center gap-1.5 text-[10px] font-medium text-emerald-700 dark:text-emerald-300 hover:text-emerald-800 dark:hover:text-emerald-200"
                      data-testid="provenance-certified-toggle">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                      ✓ Uses {cleanItems.length} certified measure{cleanItems.length === 1 ? '' : 's'}
                      {provExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    </button>
                    {provExpanded && (
                      <div className="mt-1.5 flex flex-col gap-1 pl-3" data-testid="provenance-certified-list">
                        {cleanItems.map(p => {
                          const k = certifiedLocal[p.name] ? KIND.certified : (KIND[p.kind] || KIND.certified);
                          return (
                            <div key={p.name} className="flex items-center gap-2 flex-wrap text-[10px]" data-testid={`provenance-${p.name}`}>
                              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border ${k.cls}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${k.dot}`} />{k.label}
                              </span>
                              <span className="text-foreground/85">{resolveLabel(allFields, p.name) || p.label || p.name}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
                {attentionItems.length > 0 && (
                  <div className={cleanItems.length > 0 ? 'mt-2.5' : ''}>
                    <div className="text-[10px] font-medium text-amber-800 dark:text-amber-200">These measures aren’t certified — review or certify them:</div>
                    <div className="mt-2 flex flex-col gap-1.5" data-testid="provenance-per-measure">
                      {attentionItems.map(p => {
                        const isCertified = certifiedLocal[p.name];
                        const k = isCertified ? KIND.certified : (KIND[p.kind] || KIND.ai_calculation);
                        const done = certRequested[p.name];
                        const showRequest = !isCertified;
                        return (
                          <div key={p.name} className="flex items-center gap-2 flex-wrap text-[10px]" data-testid={`provenance-${p.name}`}>
                            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border ${k.cls}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${k.dot}`} />{isCertified ? 'Certified' : k.label}
                            </span>
                            <span className="text-foreground/85">{resolveLabel(allFields, p.name) || p.label || p.name}</span>
                            {!isCertified && p.kind === 'ai_calculation' && <span className="text-muted-foreground">— computed by AI, not a governed measure</span>}
                            {!isCertified && p.kind === 'uncertified' && p.status === 'in_review' && <span className="text-amber-700 dark:text-amber-300">— in review</span>}
                            {isCertified && <span className="text-emerald-800 dark:text-emerald-300" data-testid={`cert-done-${p.name}`}>certified ✓</span>}
                            {showRequest && (done ? (
                              <span className="text-emerald-800 dark:text-emerald-300" data-testid={`cert-requested-${p.name}`}>Certification requested ✓</span>
                            ) : (
                              <>
                                {canCertify && p.measure_id && (
                                  <Button size="sm"
                                    className="h-5 px-2 text-[9px] rounded-full bg-emerald-500 hover:bg-emerald-600 text-white"
                                    disabled={certLoading[p.name]}
                                    onClick={() => certifyNow(p)}
                                    data-testid={`certify-now-${p.name}`}>
                                    {certLoading[p.name] ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Certify'}
                                  </Button>
                                )}
                                <Button size="sm" variant="outline"
                                  className="h-5 px-2 text-[9px] rounded-full border-primary/40 text-primary hover:bg-primary/10"
                                  disabled={certLoading[p.name] || !selectedModel}
                                  onClick={() => requestCert(p)}
                                  data-testid={`request-cert-${p.name}`}>
                                  {certLoading[p.name] ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Request certification'}
                                </Button>
                              </>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })() : (msg.result._certification_summary && msg.result._certification_summary.total > 0 && (() => {
            const cs = msg.result._certification_summary;
            const map = msg.result._certification || {};
            if (cs.all_certified) {
              return (
                <div className="mt-3 pt-3 border-t border-emerald-500/20 flex items-center gap-2 flex-wrap" data-testid="cert-trust-summary">
                  <CertificationBadge status="certified" size="xs" />
                  <span className="text-[10px] text-emerald-800 dark:text-emerald-300">All {cs.total} measure{cs.total === 1 ? '' : 's'} used in this answer are peer-reviewed and certified.</span>
                </div>
              );
            }
            return (
              <div className="mt-3 pt-3 border-t border-amber-500/25" data-testid="cert-trust-summary">
                <div className="flex items-center gap-2 text-[10px] text-amber-800 dark:text-amber-200 font-medium">
                  <CertificationBadge status="draft" size="xs" />
                  ⚠️ Uncertified measures included. Values are not peer-reviewed.
                </div>
                <div className="mt-1.5 text-[10px] text-amber-700 dark:text-amber-200">
                  {cs.certified} certified · {cs.uncertified} uncertified{cs.deprecated ? ` · ${cs.deprecated} deprecated` : ''} across {cs.total} measure{cs.total === 1 ? '' : 's'}.
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="cert-per-measure">
                  {(msg.result.measures || []).map(mName => {
                    const info = map[mName] || { status: 'draft' };
                    return (
                      <span key={mName} className="inline-flex items-center gap-1 text-[10px]" data-testid={`cert-measure-${mName}`}>
                        <span className="font-mono text-foreground/80">{mName}</span>
                        <CertificationBadge status={info.status} size="xs" replacedBy={info.replaced_by} />
                        {info.status === 'deprecated' && info.replaced_by && (
                          <span className="text-[9px] text-amber-700 dark:text-amber-300">→ use {info.replaced_by}</span>
                        )}
                      </span>
                    );
                  })}
                </div>
              </div>
            );
          })())}
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            {msg.result.confidence && (
              <Badge
                variant="outline"
                className={`text-[8px] cursor-help ${msg.result.confidence === 'high' ? 'border-emerald-500/40 text-emerald-800 dark:text-emerald-300' : msg.result.confidence === 'low' ? 'border-red-500/40 text-red-800 dark:text-red-300' : 'border-amber-500/40 text-amber-800 dark:text-amber-300'}`}
                title="How sure the AI is that it understood your question correctly and picked the right measures and dimensions to answer it."
                data-testid="interpretation-confidence-badge"
              >
                Interpretation: {msg.result.confidence === 'high' ? 'High' : msg.result.confidence === 'low' ? 'Low' : 'Medium'}
              </Badge>
            )}
            {msg.result._grounded && (
              <Badge variant="outline" className="text-[8px] border-cyan-500/30 text-cyan-400" title={`Narrative grounded in ${msg.result._grounded_rows || 0} data rows`} data-testid="grounded-badge">
                grounded in data
              </Badge>
            )}
            {isLowConfidence && (
              <Badge
                variant="outline"
                className="text-[8px] border-amber-500/40 text-amber-800 dark:text-amber-300 bg-amber-500/10 cursor-help"
                title={`A second AI pass reviewed this answer's reliability and scored it ${critiqueConfidence}/100 (low).${critiqueWeakness ? ' ' + critiqueWeakness : ''}`}
                data-testid="low-confidence-badge"
              >
                Answer quality: {critiqueConfidence}/100
              </Badge>
            )}
          </div>
          {isLowConfidence && critiqueWeakness && (
            <p className="text-[10px] text-amber-700 dark:text-amber-300 mt-2 italic" data-testid="critique-weakness">
              ⚠ {critiqueWeakness}
            </p>
          )}
          {isLowConfidence && critiqueFollowup && (
            <button
              onClick={() => { setQuestion(critiqueFollowup); setTimeout(() => ask(critiqueFollowup), 100); }}
              className="mt-2 inline-flex items-center gap-1.5 text-[10px] text-amber-700 dark:text-amber-300 hover:text-amber-800 dark:hover:text-amber-200 underline-offset-2 hover:underline"
              data-testid="critique-followup"
            >
              <ArrowRight className="w-2.5 h-2.5" /> Try instead: {critiqueFollowup}
            </button>
          )}
          {/* External references — any fact in the answer/narrative that
              came from the LLM's training (industry benchmark, regulation,
              definition) rather than the tenant's data. Server already
              sanitizes URLs so only trusted-domain links render clickable. */}
          {Array.isArray(msg.result?.external_facts) && msg.result.external_facts.length > 0 && (
            <div className="mt-3 pt-3 border-t border-border/20" data-testid="external-references">
              <div className="flex items-center gap-1.5 mb-1.5">
                <BookOpen className="w-3 h-3 text-muted-foreground/70" />
                <p className="text-[10px] text-muted-foreground/70 uppercase tracking-wider font-semibold">
                  External references · {msg.result.external_facts.length}
                </p>
              </div>
              <ol className="space-y-1.5 text-[10px] text-muted-foreground/80 leading-relaxed pl-3">
                {msg.result.external_facts.map((f, i) => (
                  <li key={i} className="list-decimal" data-testid={`external-ref-${i}`}>
                    <span className="text-muted-foreground">{f.claim}</span>
                    {f.source && (
                      <span className="ml-1 text-muted-foreground/80">
                        — {f.url ? (
                          <a
                            href={f.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-0.5 text-cyan-400/80 hover:text-cyan-300 underline-offset-2 hover:underline"
                            data-testid={`external-ref-link-${i}`}
                          >
                            {f.source}
                            <ExternalLink className="w-2.5 h-2.5" />
                          </a>
                        ) : (
                          <span className="italic">{f.source}</span>
                        )}
                      </span>
                    )}
                  </li>
                ))}
              </ol>
              <p className="text-[9px] text-muted-foreground/70 italic mt-2 leading-tight">
                External facts come from the AI's training data, not your tenant. Links shown only when the source is verified (gov, NAIC, Wikipedia, IRS, etc.).
              </p>
            </div>
          )}
        </div>
      )}

      {/* Forecast */}
      {!isClarify && msg.result?.output_type === 'forecast' && msg.result?.forecast && (msg.result.forecast.series?.length > 0 || msg.result.forecast.groups?.length > 0) && (
        <div className="relative" data-testid="explore-forecast-block">
          {vizToolbar}
          <ForecastChart forecast={msg.result.forecast} formatValue={formatAxis} />
        </div>
      )}

      {/* Certified Scope Guard chip: shows a certified measure's built-in scope
          that was re-applied to the AI's SQL (transparency), or a note when a
          multi-scope query couldn't be auto-verified. */}
      {!isClarify && (msg.result?.scope_enforced?.length > 0 || msg.result?.scope_unverified) && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="scope-guard-bar">
          {(msg.result?.scope_enforced || []).map((p, si) => (
            <span key={`scope-${si}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] border bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-300" data-testid={`scope-enforced-chip-${si}`} title="A certified measure's built-in scope was enforced on this query">
              <ShieldCheck className="w-2.5 h-2.5" /> Scope enforced: {p}
            </span>
          ))}
          {msg.result?.scope_unverified && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] border bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-300" data-testid="scope-unverified-chip" title="This query combines measures with different scopes, so the scope couldn't be auto-verified">
              <AlertTriangle className="w-2.5 h-2.5" /> Scope not auto-verified
            </span>
          )}
        </div>
      )}

      {/* Filter bar — Type A context chips + Type B interactive dimension filter */}
      {!isClarify && msg.liveData && msg.liveData.length > 0 && (msg.result?.dimensions?.length > 0) && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="explore-filter-bar">
          {/* Type A: filters the natural-language query already applied.
              Click a chip to see the field's actual values and swap quickly. */}
          {queryFilters.map((f, fi) => (
            <div key={`qf-${fi}`} className="relative">
              <button
                type="button"
                onClick={() => openValueHint(f, fi)}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] border transition-colors ${f.exclude ? 'bg-rose-500/10 border-rose-500/30 text-rose-700 dark:text-rose-300' : 'bg-sky-500/10 border-sky-500/30 text-sky-700 dark:text-sky-300'} hover:brightness-110`}
                data-testid={`query-filter-chip-${fi}`}
                title="Filter applied by your question — click to see other values"
              >
                <Filter className="w-2.5 h-2.5" />
                <span className="font-medium">{resolveLabel(allFields, f.col) || f.col}</span>
                <span className="opacity-70">{f.exclude ? '≠' : (f.op || '=')}</span>
                <span>{f.values.slice(0, 3).join(', ')}{f.values.length > 3 ? ` +${f.values.length - 3}` : ''}</span>
                <ChevronDown className="w-2.5 h-2.5 opacity-60" />
              </button>
              {valueHint.open === fi && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setValueHint({ open: null, loading: false, values: [], reason: '' })} aria-hidden />
                  <div className="absolute left-0 top-full mt-1 z-50 w-56 rounded-lg border border-border/50 bg-popover shadow-lg py-1 max-h-64 overflow-auto" data-testid={`filter-hint-menu-${fi}`}>
                    <p className="px-3 py-1 text-[9px] uppercase tracking-wider text-muted-foreground/80">
                      {resolveLabel(allFields, f.col) || f.col} — values
                    </p>
                    {valueHint.loading && (
                      <div className="px-3 py-2 text-[11px] text-muted-foreground flex items-center gap-1.5" data-testid="filter-hint-loading"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>
                    )}
                    {!valueHint.loading && valueHint.values.length === 0 && (
                      <div className="px-3 py-2 text-[11px] text-muted-foreground" data-testid="filter-hint-empty">
                        {valueHint.reason === 'pii' ? 'Values hidden — PII field'
                          : valueHint.reason === 'unsupported_source' || valueHint.reason === 'no_datasource' ? 'Values not available for this source'
                          : 'No values found'}
                      </div>
                    )}
                    {!valueHint.loading && valueHint.values.map(v => {
                      const active = (f.values || []).map(String).includes(String(v));
                      return (
                        <button
                          key={v}
                          onClick={() => pickHintValue(f, v)}
                          className={`w-full flex items-center gap-2 px-3 py-1.5 text-[11px] text-left hover:bg-accent/40 ${active ? 'text-primary font-medium' : ''}`}
                          data-testid={`filter-hint-value-${v}`}
                        >
                          {active ? <Check className="w-3 h-3 flex-shrink-0" /> : <span className="w-3 h-3 flex-shrink-0" />}
                          <span className="truncate">{v}</span>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          ))}

          {/* Type B: user-added filter on a dimension that's on the chart */}
          <div className="relative">
            <button onClick={() => { setFieldMenuOpen(o => !o); setValueMenuOpen(false); }} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] border border-border/50 text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors" data-testid="add-filter-btn" title="Filter the chart by a field">
              <Filter className="w-2.5 h-2.5" /> Filter
            </button>
            {fieldMenuOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setFieldMenuOpen(false)} aria-hidden />
                <div className="absolute left-0 top-full mt-1 z-50 w-52 rounded-lg border border-border/50 bg-popover shadow-lg py-1 max-h-56 overflow-auto" data-testid="filter-field-menu">
                  <p className="px-3 py-1 text-[9px] uppercase tracking-wider text-muted-foreground/80">Filter by field</p>
                  {(msg.result.dimensions || []).map(d => (
                    <button key={d} onClick={() => { setBFilterField(d); setBFilterValues([]); setFieldMenuOpen(false); setValueMenuOpen(true); }} className="w-full text-left px-3 py-1.5 text-[11px] hover:bg-accent/40 truncate" data-testid={`filter-field-${d}`}>
                      {resolveLabel(allFields, d) || d}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Value dropdown (top of chart) once a field is chosen */}
          {bFilterField && (
            <div className="relative">
              <button onClick={() => setValueMenuOpen(o => !o)} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] border border-primary/40 bg-primary/5 text-foreground" data-testid="filter-value-trigger">
                <span className="font-medium">{resolveLabel(allFields, bFilterField) || bFilterField}</span>
                <span className="opacity-70">{bFilterValues.length ? `: ${bFilterValues.length} selected` : ': all'}</span>
                <ChevronDown className="w-2.5 h-2.5" />
              </button>
              <button onClick={clearBFilter} className="ml-1 text-muted-foreground/80 hover:text-destructive" data-testid="filter-clear-btn" title="Remove filter"><X className="w-3 h-3" /></button>
              {valueMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setValueMenuOpen(false)} aria-hidden />
                  <div className="absolute left-0 top-full mt-1 z-50 w-56 rounded-lg border border-border/50 bg-popover shadow-lg py-1 max-h-64 overflow-auto" data-testid="filter-value-menu">
                    <div className="flex items-center justify-between px-3 py-1 border-b border-border/40">
                      <span className="text-[9px] uppercase tracking-wider text-muted-foreground/80">Show values</span>
                      <button onClick={() => setBFilterValues([])} className="text-[10px] text-primary hover:underline">All</button>
                    </div>
                    {bFilterOptions.map(v => (
                      <button key={v} onClick={() => toggleBValue(v)} className="w-full flex items-center gap-2 px-3 py-1.5 text-[11px] hover:bg-accent/40 text-left" data-testid={`filter-value-${v}`}>
                        <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${bFilterValues.includes(v) ? 'bg-primary border-primary text-primary-foreground' : 'border-border/60'}`}>{bFilterValues.includes(v) && <Check className="w-2.5 h-2.5" />}</span>
                        <span className="truncate">{v}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}


      {/* KPI */}
      {!isClarify && msg.liveData && msg.liveData.length > 0 && msg.result?.output_type === 'kpi' && msg.result.measures?.length > 0 && (
        <div className="glass-card rounded-2xl p-5 border border-border/30 relative">
          {vizToolbar}
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">
            {resolveLabel(allFields, msg.result.measures[0])}
            {kpiUnitLabel && <span className="ml-1.5 text-muted-foreground/70">{kpiUnitLabel}</span>}
          </p>
          <p className="text-3xl font-heading font-bold text-foreground mt-1" data-testid="kpi-value">{renderKpi(msg.liveData[0]?.[msg.result.measures[0]])}</p>
        </div>
      )}

      {/* Chart */}
      {msg.liveData && msg.liveData.length > 0 && effectiveChartType && msg.result.dimensions?.length > 0 && !forceTable && (forceChart || msg.result.output_type !== 'table') && (
        <div className="glass-card rounded-2xl p-4 border border-border/30 overflow-hidden relative" data-testid="explore-live-chart" onContextMenu={onChartContextMenu}>
          {buildToolbar(
            <button type="button" onClick={() => setChartHelpOpen(v => !v)} title="Chart commands you can type"
              className="p-1.5 rounded hover:bg-muted/40 text-muted-foreground/70 hover:text-primary transition-colors" data-testid="chart-help-toggle">
              <HelpCircle className="w-3.5 h-3.5" />
            </button>
          )}
          {chartHelpOpen && (
            <div className="absolute top-9 right-3 z-30 w-64 rounded-xl border border-border/50 bg-popover shadow-2xl p-3 text-[11px] text-foreground" data-testid="chart-help-popover">
              <p className="font-semibold mb-1.5 flex items-center justify-between">Try typing… <button onClick={() => setChartHelpOpen(false)} className="text-muted-foreground hover:text-foreground"><X className="w-3 h-3" /></button></p>
              <ul className="space-y-1 text-muted-foreground">
                <li>“colour by line of business”</li>
                <li>“one line per year” / “merge into one line”</li>
                <li>“show data labels” · “hide legend”</li>
                <li>“start the axis at zero” · “log scale”</li>
                <li>“show in millions” · “round to whole numbers”</li>
                <li>“add an average line” · “target line at 100”</li>
                <li>“highlight 2023” · “make it a bar chart”</li>
                <li>Type <span className="text-primary font-mono">/</span> for the full menu</li>
              </ul>
            </div>
          )}
          <div className="w-full overflow-hidden explore-chart-capture" data-viz-capture={idx} style={{ height: 350 }}>
            {(() => {
              const dims = msg.result.dimensions;
              const measures = msg.result.measures || [];
              const xAxis = msg.result.x_axis || null;
              let colorBy = vizState.combineSeries ? null : (vizState.colorBy || msg.result.color_by || null);
              // A line chart with two dimensions reads best as multiple coloured
              // lines: put the time dimension on the x-axis and colour by the
              // other dimension (so "premium by LOB and year" = one line per LOB).
              if (!colorBy && !vizState.combineSeries && effectiveChartType === 'line_chart' && dims.length === 2 && measures.length >= 1) {
                const dateDim = dims.find(d => dateGrains[d]);
                colorBy = dateDim ? dims.find(d => d !== dateDim) : dims[1];
              }
              const effXAxis = colorBy ? (dims.find(d => d !== colorBy) || xAxis) : xAxis;
              const _hlField = vizState.highlightValue ? (colorBy || dims[0]) : undefined;
              const pivot = colorBy ? pivotForChart(applyBFilter(msg.liveData), dims, measures, effXAxis, colorBy) : null;
              if (pivot) {
                return <ChartRenderer vizType={effectiveChartType} data={transformRows(pivot.data)} rowDims={[pivot.xDim]} colDim="" valueMeasures={pivot.seriesKeys} showGrid={vizShowGrid} showLegend={vizShowLegend} vizConfig={vizConfig} themeColors={vizThemeColors} refLineValue={vizRefLineValue} refLineLabel={vizRefLineLabel} secondYAxis={vizState.secondAxis} highlightField={vizState.highlightValue ? colorBy : undefined} highlightValue={vizState.highlightValue || undefined} formatValue={chartFormatAxis} allFields={allFields} fieldOverrides={fieldOverrides} onDrill={() => {}} onHover={(p) => { lastHoverRef.current = p; }} marks={{}} />;
              }
              const orderedDims = xAxis && dims.includes(xAxis) ? [xAxis, ...dims.filter(d => d !== xAxis)] : dims;
              return <ChartRenderer vizType={effectiveChartType} data={transformRows(applyBFilter(msg.liveData).slice(0, 200))} rowDims={orderedDims} colDim="" valueMeasures={measures} showGrid={vizShowGrid} showLegend={vizShowLegend} vizConfig={vizConfig} themeColors={vizThemeColors} refLineValue={vizRefLineValue} refLineLabel={vizRefLineLabel} secondYAxis={vizState.secondAxis} highlightField={_hlField} highlightValue={vizState.highlightValue || undefined} formatValue={chartFormatAxis} allFields={allFields} fieldOverrides={fieldOverrides} onDrill={() => {}} onHover={(p) => { lastHoverRef.current = p; }} marks={{}} />;
            })()}
          </div>
          {startDiscussion && <p className="text-[9px] text-muted-foreground/70 mt-1.5" data-testid="discuss-point-hint">Tip: right-click a data point to start a discussion about it.</p>}
        </div>
      )}

      {/* Table */}
      {msg.liveData && msg.liveData.length > 0 && !(effectiveChartType && msg.result.dimensions?.length > 0 && !forceTable && (forceChart || msg.result.output_type !== 'table')) && (forceTable || msg.result?.output_type === 'table' || (!effectiveChartType && msg.result?.dimensions?.length > 0)) && (
        <div className="glass-card rounded-2xl border border-border/30 relative" data-testid="explore-live-table">
          {vizToolbar}
          <div className="max-h-64 overflow-auto p-3 pt-10">
            {(() => {
              const tableRows = markAnomalies(markRatioGuardrails(transformRows(applyBFilter(msg.liveData))));
              const anomalyCount = tableRows.filter(r => r._anomaly).length;
              return (
                <>
                  {anomalyCount > 0 && (
                    <div className="flex items-center gap-1.5 mb-2 px-2 py-1 rounded-md bg-amber-500/10 border border-amber-500/20 text-amber-500 text-[11px] font-medium w-fit" data-testid="anomaly-summary">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                      {anomalyCount} outlier{anomalyCount > 1 ? 's' : ''} detected — highlighted row{anomalyCount > 1 ? 's' : ''} below
                    </div>
                  )}
                  <TableRenderer result={{ data: tableRows }} vizConfig={{ maxRows: vizState.topN || 50 }} formatValue={formatValueWithUnits} allFields={allFields} fieldOverrides={fieldOverrides} rowDims={msg.result.dimensions || []} onDrill={() => {}} onAnomalyClick={(row) => setAnomalyDetail(row)} onRowContextMenu={startDiscussion ? (row) => discussDataPoint(row) : undefined} rowTrends={trendOn ? trendSeries : null} rowTrendLabels={trendOn ? (trendMeta?.labels || null) : null} rowTrendTarget={trendOn ? (trendMeta?.target || null) : null} />
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Anomaly drill-in — quick "why is this an outlier" breakdown */}
      {anomalyDetail && anomalyDetail._anomalyStats && createPortal(
        (() => {
          const s = anomalyDetail._anomalyStats;
          const val = Number(anomalyDetail[s.target]);
          const dimVal = s.dim ? anomalyDetail[s.dim] : '';
          const fmtN = (n) => formatValueWithUnits(n, s.target);
          const beyond = anomalyDetail._anomaly === 'high' ? (s.iqr ? (val - s.q3) / s.iqr : 0) : (s.iqr ? (s.q1 - val) / s.iqr : 0);
          const vsMedianPct = s.median ? ((val / s.median - 1) * 100) : null;
          const dir = anomalyDetail._anomaly === 'high' ? 'above' : 'below';
          return (
            <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/50 p-4" onClick={() => setAnomalyDetail(null)} data-testid="anomaly-detail-dialog">
              <div className="bg-popover border border-border/50 rounded-xl shadow-2xl w-full max-w-sm p-4" onClick={e => e.stopPropagation()}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-amber-500 text-lg">{'\u26A0'}</span>
                  <div className="text-sm font-semibold text-foreground">Why is this an outlier?</div>
                </div>
                <div className="text-xs text-foreground/90 mb-3">
                  <span className="font-medium">{String(dimVal)}</span> has {s.targetLabel} of{' '}
                  <span className="font-semibold text-amber-500">{fmtN(val)}</span> — well {dir} the typical range for the other {s.dimLabel ? s.dimLabel.toLowerCase() : 'row'} values.
                </div>
                <div className="space-y-1.5 text-[11px]">
                  {[
                    ['This value', fmtN(val)],
                    ['Median (typical)', fmtN(s.median)],
                    ['Average', fmtN(s.mean)],
                    ['Typical range (Q1–Q3)', `${fmtN(s.q1)} – ${fmtN(s.q3)}`],
                    ['Outlier fence', anomalyDetail._anomaly === 'high' ? `above ${fmtN(s.hiFence)}` : `below ${fmtN(s.loFence)}`],
                  ].map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between gap-3">
                      <span className="text-muted-foreground">{k}</span>
                      <span className="text-foreground font-medium">{v}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 p-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-[11px] text-foreground/80" data-testid="anomaly-detail-explain">
                  It sits <span className="font-semibold">{beyond.toFixed(1)}×</span> the interquartile range beyond the {anomalyDetail._anomaly === 'high' ? 'upper' : 'lower'} quartile
                  {vsMedianPct != null && Number.isFinite(vsMedianPct) ? <> and is <span className="font-semibold">{Math.abs(vsMedianPct).toFixed(0)}%</span> {vsMedianPct >= 0 ? 'above' : 'below'} the median</> : null}.
                  <div className="text-muted-foreground mt-1">Sensitivity: {anomalyStrictness} (Tukey k={s.mult}). Adjust with the Strict/Balanced/More control.</div>
                </div>
                <div className="flex justify-end gap-2 mt-3">
                  {startDiscussion && (
                    <button onClick={() => { const row = anomalyDetail; setAnomalyDetail(null); discussDataPoint({ ...row, _issue: 'flagged anomaly' }); }} className="px-3 py-1.5 text-xs rounded-md bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30 font-medium flex items-center gap-1.5" data-testid="anomaly-discuss-btn" title="Start a discussion flagged as a data issue">
                      <MessageSquare className="w-3.5 h-3.5" /> Discuss as data issue
                    </button>
                  )}
                  <button onClick={() => setAnomalyDetail(null)} className="px-3 py-1.5 text-xs rounded-md bg-primary text-primary-foreground font-medium" data-testid="anomaly-detail-close">Close</button>
                </div>
              </div>
            </div>
          );
        })(),
        document.body
      )}

      {/* No live data fallback — fires whenever the AI intended a
          visualisation (chart or table) but liveData is empty or the
          underlying query failed. Without this the user sees only the
          narrative and has no way to inspect the SQL or the failure. */}
      {(!msg.liveData || msg.liveData.length === 0) && msg.result?.output_type !== 'forecast' &&
        (msg.result?.chart_type || msg.result?.output_type === 'table' || msg.result?.dimensions?.length > 0 || msg.queryError) && (
        <div className="glass-card rounded-2xl p-4 border border-border/30 relative" data-testid="explore-empty-fallback">
          {vizToolbar}
          <div className="flex items-center gap-2 mb-2">
            <BarChart3 className="w-3.5 h-3.5 text-primary/60" />
            <span className="text-[10px] text-muted-foreground">
              {msg.result?.chart_type
                ? `${msg.result.chart_type.replace('_', ' ')} — ${(msg.result.dimensions || []).join(', ')} x ${(msg.result.measures || []).join(', ')}`
                : msg.result?.output_type === 'table'
                  ? `table — ${(msg.result.dimensions || []).join(', ') || 'no dimensions'} x ${(msg.result.measures || []).join(', ') || 'no measures'}`
                  : 'query result'}
            </span>
          </div>
          {msg.queryError ? (
            <div className="p-3 rounded-xl bg-red-500/5 border border-red-500/20 text-xs text-red-800 dark:text-red-300" data-testid="explore-query-error">
              <p className="font-medium mb-1">Query failed</p>
              <p className="text-[10px] text-red-700 dark:text-red-300/80">{msg.queryError}</p>
              {(msg.debugQuery?.sql || msg.result?.sql) ? (
                <div className="mt-2" data-testid="explore-error-sql">
                  <p className="text-[10px] text-muted-foreground/80 mb-1">The query that was attempted:</p>
                  <pre className="text-[10px] leading-relaxed bg-muted/40 border border-border/30 rounded-lg p-2 overflow-x-auto whitespace-pre-wrap text-foreground/80">{msg.debugQuery?.sql || msg.result?.sql}</pre>
                </div>
              ) : (
                <p className="text-[10px] text-muted-foreground/80 mt-2">Click the code icon above to view the query that was attempted.</p>
              )}
              {isAdmin && onTryAdvancedSql && (
                <button onClick={() => onTryAdvancedSql(msg.question || '')} className="mt-2 text-[10px] text-amber-700 dark:text-amber-300 hover:text-amber-800 dark:hover:text-amber-200 underline underline-offset-2" data-testid="try-advanced-sql-error">
                  Try Advanced SQL instead →
                </button>
              )}
            </div>
          ) : Array.isArray(msg.liveData) ? (
            <div className="flex flex-col items-center justify-center gap-2 rounded-xl bg-muted/10 border border-border/20 text-xs text-muted-foreground/80 text-center px-4 py-6" data-testid="explore-zero-rows">
              <span>No rows returned for this combination of fields — try removing a filter or a dimension.</span>
              {isAdmin && onTryAdvancedSql && (
                <button onClick={() => onTryAdvancedSql(msg.question || '')} className="text-[10px] text-amber-700 dark:text-amber-300 hover:text-amber-800 dark:hover:text-amber-200 underline underline-offset-2" data-testid="try-advanced-sql-zero">
                  Try Advanced SQL instead →
                </button>
              )}
            </div>
          ) : (msg.result?.dimensions?.length === 0 && msg.result?.measures?.length === 0) ? (
            <div className="h-32 flex items-center justify-center rounded-xl bg-muted/10 border border-border/20 text-xs text-muted-foreground/80 text-center px-4" data-testid="explore-no-fields">
              The AI answered from context but didn&apos;t pick specific fields — click the code icon above to see what it tried, or refine your question with a field name.
            </div>
          ) : (
            <div className="h-32 flex items-center justify-center rounded-xl bg-muted/10 border border-border/20 text-xs text-muted-foreground/80" data-testid="explore-no-live-data">
              Could not load live data — click &quot;Save as Insight&quot; to build manually
            </div>
          )}
        </div>
      )}

      {/* Definitions used */}
      {msg.result?.definitions_used?.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {msg.result.definitions_used.map((d, di) => (
            <Badge key={di} variant="outline" className="text-[8px] border-border/30" title={d.definition}>{d.term}</Badge>
          ))}
        </div>
      )}

      {/* Assumptions — shown as "we assumed X" chips; tap to correct in one go */}
      {msg.result?.assumptions?.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mt-1" data-testid="assumption-chips">
          <span className="text-[10px] text-muted-foreground/80">We assumed:</span>
          {msg.result.assumptions.map((a, i) => (
            <button
              key={i}
              onClick={() => {
                try { api.recordClarifyPick({ kind: 'assumption', label: String(a).slice(0, 80), model_id: modelId }); } catch (e) { /* non-blocking */ }
                const txt = String(a).toLowerCase();
                const base = msg.result.base_question || msg.userQuestion || '';
                if (onOpenAsOf && /\b(as of|as-of|today|date|ytd|qtd|mtd|itd|period|month|quarter|year)\b/.test(txt)) {
                  onOpenAsOf();
                } else if (onDisambiguate && base) {
                  // Jump straight to the exact-field control: re-ask the base
                  // question and force the measure/geography clarify card.
                  onDisambiguate(base, String(a));
                } else if (onBuildMeasure && /\b(measure|premium|ratio|loss|margin|revenue|count)\b/.test(txt)) {
                  onBuildMeasure();
                } else {
                  setQuestion(base ? `${base} — not ${a}` : String(a));
                }
              }}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-border/40 bg-muted/20 text-[10px] text-muted-foreground hover:border-amber-500/40 hover:text-amber-700 dark:hover:text-amber-200 transition-colors"
              title="Assumed automatically — tap to change"
              data-testid={`assumption-chip-${i}`}
            >
              {a} <Pencil className="w-2.5 h-2.5 opacity-60" />
            </button>
          ))}
        </div>
      )}

      {/* Follow-up suggestion pills — clickable questions right under the chart */}
      {msg.result?.follow_up_suggestions?.length > 0 && idx === messagesLength - 1 && !isSharedSession && (
        <div className="space-y-1.5" data-testid="followup-suggestions">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground/80 font-semibold">Explore further</p>
          <div className="flex flex-wrap gap-1.5">
            {msg.result.follow_up_suggestions.slice(0, 4).map((s, si) => (
              <button key={si} onClick={() => { setQuestion(s); setTimeout(() => ask(s), 100); }} data-testid={`followup-pill-${si}`} className="px-3 py-1.5 rounded-full border border-primary/20 bg-primary/5 hover:border-primary/40 hover:bg-primary/10 text-[10px] text-muted-foreground hover:text-foreground transition-all">
                <ArrowRight className="w-2.5 h-2.5 inline mr-1" />{s}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Action bar */}
      <div className="flex flex-wrap items-center gap-1.5">
        {/* Thumbs up/down */}
        <div className="flex items-center gap-0.5 mr-1">
          <button onClick={handleThumbsUp} disabled={feedbackState === 'up'} className={`p-1.5 rounded-lg transition-colors ${feedbackState === 'up' ? 'bg-emerald-500/20 text-emerald-400' : 'text-muted-foreground/70 hover:text-emerald-400 hover:bg-emerald-500/10'}`} title="Good response — saves the query to the model's SQL Library so the AI reuses it" data-testid="thumbs-up">
            <ThumbsUp className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => { if (feedbackState !== 'down') setShowDownForm(s => !s); }} disabled={feedbackState === 'down'} className={`p-1.5 rounded-lg transition-colors ${feedbackState === 'down' ? 'bg-red-500/20 text-red-400' : 'text-muted-foreground/70 hover:text-red-400 hover:bg-red-500/10'}`} title="Bad response — tell us why and we'll retry" data-testid="thumbs-down">
            <ThumbsDown className="w-3.5 h-3.5" />
          </button>
        </div>

        <Button variant="outline" size="sm" className="rounded-lg text-[10px] h-7 gap-1" title="Get notified when this metric crosses a threshold you set" onClick={() => createAlert(msg.result)} data-testid="action-create-alert">
          <Bell className="w-3 h-3" /> Create Alert
        </Button>
        {can('ai_copilot:quick_investigation') && (
        <Button variant="outline" size="sm" className="rounded-lg text-[10px] h-7 gap-1" title="Governed diagnosis: quantified drivers, tested & ruled-out, every number validated — no data leaves to external AI" onClick={() => (typeof runInvestigate === 'function' ? runInvestigate : runRCA)(`Why did ${msg.result?.measures?.[0] ? resolveLabel(allFields, msg.result.measures[0]) : 'this metric'} change?`)} disabled={investigateLoading || rcaLoading} data-testid="action-investigate">
          {(investigateLoading || rcaLoading) ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />} Investigate
        </Button>
        )}
        {startFullInvestigation && can('ai_copilot:full_investigation') && (
          <Button variant="outline" size="sm" className="rounded-lg text-[10px] h-7 gap-1 border-indigo-500/40 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-500/10" title={`Open a full, saved ${getTerm('investigations', 'singular')} for deeper, trackable root-cause analysis`} onClick={() => startFullInvestigation(msg)} data-testid="action-full-investigation">
            <Sparkles className="w-3 h-3" /> Full Investigation
          </Button>
        )}
        {can('ai_copilot:ai_response') && (
          <Button variant="outline" size="sm" className="rounded-lg text-[10px] h-7 gap-1" title="View the raw AI response and query plan behind this answer" onClick={() => setAiRawView && setAiRawView(msg.result)} data-testid="action-view-ai-raw">
            <Cpu className="w-3 h-3" /> AI Response
          </Button>
        )}
        <Button variant="outline" size="sm" className="rounded-lg text-[10px] h-7 gap-1" title="Start a discussion thread to comment on this result with your team" onClick={() => startDiscussion && startDiscussion(msg)} data-testid="action-start-thread">
          <MessageSquare className="w-3 h-3" /> Start Thread
        </Button>
        {discEntry?.total > 0 && (
          <Badge variant="outline" className="text-[9px] h-7 px-2 flex items-center gap-1 bg-primary/10 text-primary border-primary/20 cursor-pointer" onClick={() => navigate(`/threads?id=${discEntry.discussion_id}`)} data-testid="explore-discussion-indicator">
            <MessageSquare className="w-2.5 h-2.5" />{discEntry.total} discussion{discEntry.total === 1 ? '' : 's'}
          </Badge>
        )}
      </div>

      {/* Thumbs-down loop closer: after a feedback retry, offer to teach the fix */}
      {msg.revisedFromFeedback && !showDownForm && feedbackState !== 'down' && (
        fixOutcome ? (
          <div className="flex items-center gap-1.5 text-[11px] text-emerald-700 dark:text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-2.5 py-1.5" data-testid="teach-confirmation">
            <Check className="w-3.5 h-3.5 flex-shrink-0" />
            {fixOutcome === 'suggested'
              ? 'Sent to a steward to approve — AI Explore will reuse it once approved.'
              : 'Saved — AI Explore will reuse this for similar questions.'}
          </div>
        ) : (
          <div className="glass-card rounded-xl p-3 border border-emerald-500/20 bg-emerald-500/5 space-y-2" data-testid="did-this-fix-it">
            <p className="text-[11px] font-medium text-foreground">Did this fix it?</p>
            {(msg.result?.sql_mode && msg.result?.sql && !msg.result?.error) ? (
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={handleTeach} className="rounded-lg text-[10px] h-7 bg-emerald-500/80 hover:bg-emerald-500" data-testid="teach-yes">
                  <Check className="w-3 h-3 mr-1" /> Yes — teach AI Explore
                </Button>
                <Button variant="ghost" size="sm" onClick={() => { setShowDownForm(true); }} className="rounded-lg text-[10px] h-7 border border-red-500/30 text-red-600 dark:text-red-400 hover:bg-red-500/10" data-testid="teach-still-wrong">
                  <ThumbsDown className="w-3 h-3 mr-1" /> Still wrong — try again
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-[10px] text-muted-foreground" data-testid="teach-unavailable">Teaching isn't available for this model type (no saved query to store), but your feedback was recorded.</p>
                <Button variant="ghost" size="sm" onClick={() => { setShowDownForm(true); }} className="rounded-lg text-[10px] h-7 border border-red-500/30 text-red-600 dark:text-red-400 hover:bg-red-500/10" data-testid="teach-still-wrong">
                  <ThumbsDown className="w-3 h-3 mr-1" /> Still wrong — try again
                </Button>
              </div>
            )}
          </div>
        )
      )}

      {/* Thumbs down feedback form */}
      {showDownForm && !feedbackState && (
        <div className="glass-card rounded-xl p-3 border border-red-500/20 bg-red-500/5 space-y-2" data-testid="feedback-form">
          <p className="text-[10px] text-red-700 dark:text-red-400 font-medium">What was wrong with this response?</p>
          <div className="flex flex-wrap gap-1.5" data-testid="reject-reason-codes">
            {[
              ['wrong_measure', 'Wrong metric'],
              ['wrong_dimension', 'Wrong breakdown'],
              ['wrong_filter', 'Wrong/missing filter'],
              ['wrong_time_period', 'Wrong time period'],
              ['wrong_chart', 'Wrong chart type'],
              ['incomplete', 'Incomplete'],
              ['data_looks_wrong', 'Numbers look wrong'],
              ['other', 'Something else'],
            ].map(([code, label]) => (
              <button key={code} onClick={() => setRejectCode(code)}
                className={`text-[10px] px-2 py-1 rounded-full border transition-colors ${rejectCode === code ? 'bg-red-500/20 border-red-500/50 text-red-700 dark:text-red-300' : 'border-border/40 text-muted-foreground hover:bg-muted/40'}`}
                data-testid={`reject-code-${code}`}>
                {label}
              </button>
            ))}
          </div>
          <Input
            value={downReason}
            onChange={e => setDownReason(e.target.value)}
            placeholder="Optional detail — e.g. should group by quarter not month"
            className="text-[11px] bg-muted/20 border-border/30 rounded-lg"
            onKeyDown={e => { if (e.key === 'Enter') handleThumbsDown(); }}
            data-testid="feedback-reason"
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={handleThumbsDown} disabled={feedbackLoading || !rejectCode} className="rounded-lg text-[10px] h-7 bg-red-500/80 hover:bg-red-500" data-testid="submit-feedback">
              {feedbackLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <ThumbsDown className="w-3 h-3 mr-1" />}
              Submit
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setShowDownForm(false)} className="rounded-lg text-[10px] h-7">Cancel</Button>
          </div>
        </div>
      )}

      {rejectOutcome?.kind === 'dq' && (
        <div className="glass-card rounded-xl p-3 border border-amber-500/25 bg-amber-500/5 flex items-start gap-2" data-testid="reject-dq">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-500" />
          <p className="text-[11px] text-amber-800 dark:text-amber-200 leading-relaxed">{rejectOutcome.message}</p>
        </div>
      )}

      {rejectOutcome?.kind === 'revision' && !showDownForm && (
        <div className="glass-card rounded-xl p-3 border border-sky-500/25 bg-sky-500/5 space-y-2" data-testid="reject-revision">
          <p className="text-[10px] font-medium text-sky-700 dark:text-sky-300 flex items-center gap-1.5">
            <RefreshCw className="w-3 h-3" /> {rejectOutcome.message}
          </p>
          {rejectOutcome.plan_diff?.changed ? (
            <div className="text-[11px] space-y-1" data-testid="plan-diff">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">What changed</p>
                {(() => {
                  const pd = rejectOutcome.plan_diff || {};
                  const changed = ['measures', 'dimensions', 'filters'].reduce((n, k) => n + (((pd[k] && pd[k].added) || []).length), 0);
                  const removed = ['measures', 'dimensions', 'filters'].reduce((n, k) => n + (((pd[k] && pd[k].removed) || []).length), 0);
                  const chartChanged = pd.chart && (pd.chart.before || '') !== (pd.chart.after || '');
                  const parts = [];
                  if (changed) parts.push(`changed ${changed}`);
                  if (removed) parts.push(`removed ${removed}`);
                  if (chartChanged) parts.push('chart');
                  if (!parts.length) return null;
                  return <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-sky-500/10 border border-sky-500/30 text-sky-700 dark:text-sky-300" data-testid="plan-diff-summary">{parts.join(' · ')}</span>;
                })()}
              </div>
              {['measures', 'dimensions', 'filters'].map((k) => {
                const d = rejectOutcome.plan_diff[k] || {};
                if (!(d.added || []).length && !(d.removed || []).length) return null;
                return (
                  <div key={k} className="flex flex-wrap items-center gap-1.5" data-testid={`plan-diff-${k}`}>
                    <span className="text-muted-foreground capitalize">{k}:</span>
                    {(d.added || []).map((x) => (
                      <span key={`a-${x}`} className="px-1.5 py-0.5 rounded-full text-[10px] bg-emerald-500/10 border border-emerald-500/30 text-emerald-700 dark:text-emerald-300">+ {String(x).replace(/_/g, ' ')}</span>
                    ))}
                    {(d.removed || []).map((x) => (
                      <span key={`r-${x}`} className="px-1.5 py-0.5 rounded-full text-[10px] bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-300 line-through">{String(x).split('|')[0].replace(/_/g, ' ')}</span>
                    ))}
                  </div>
                );
              })}
              {rejectOutcome.plan_diff.chart && (
                <div className="flex items-center gap-1.5" data-testid="plan-diff-chart">
                  <span className="text-muted-foreground">Chart:</span>
                  <span className="text-rose-600 line-through">{rejectOutcome.plan_diff.chart.before || '—'}</span>
                  <ArrowRight className="w-3 h-3 text-muted-foreground" />
                  <span className="text-emerald-600">{rejectOutcome.plan_diff.chart.after || '—'}</span>
                </div>
              )}
            </div>
          ) : rejectOutcome.result ? (
            <p className="text-[11px] text-muted-foreground" data-testid="plan-diff-none">No plan change was needed.</p>
          ) : null}
          {rejectOutcome.result?.answer && (
            <div className="pt-1.5 border-t border-sky-500/15">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">Revised answer</p>
              <p className="text-[11px] text-foreground leading-relaxed" data-testid="reject-revised-answer">{rejectOutcome.result.answer}</p>
            </div>
          )}
          {rejectOutcome.result?.sql && (
            <div className="pt-1">
              <button onClick={() => setShowRevSql((v) => !v)} className="text-[10px] text-sky-700 dark:text-sky-300 hover:underline flex items-center gap-1" data-testid="reject-view-sql-btn">
                <Code2 className="w-3 h-3" /> {showRevSql ? 'Hide SQL' : 'View SQL'}
              </button>
              {showRevSql && (
                <pre className="mt-1 max-h-56 overflow-auto rounded-lg bg-[#0d1117] border border-border/50 p-2.5 text-[10px] leading-relaxed text-zinc-200 font-mono whitespace-pre-wrap break-words" data-testid="reject-revised-sql">{rejectOutcome.result.sql}</pre>
              )}
            </div>
          )}
          {rejectOutcome.correction_id && rejectOutcome.result && (
            <div className="flex items-center gap-2 pt-1">
              {rejectOutcome.accepted ? (
                <p className="text-[10px] text-emerald-600 dark:text-emerald-400 flex items-center gap-1" data-testid="correction-accepted">
                  <CheckCircle2 className="w-3 h-3" /> Saved — AI Explore will remember this
                </p>
              ) : (
                <Button size="sm" onClick={handleAcceptCorrection}
                  className="rounded-lg text-[10px] h-7 bg-emerald-500/80 hover:bg-emerald-500"
                  data-testid="accept-correction">
                  <CheckCircle2 className="w-3 h-3 mr-1" /> This is correct — remember it
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={handleRejectAgain}
                className="rounded-lg text-[10px] h-7 border-red-500/40 text-red-600 dark:text-red-300 hover:bg-red-500/10"
                data-testid="reject-again">
                <ThumbsDown className="w-3 h-3 mr-1" /> Still wrong
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Error */}
      {msg.error && <p className="text-xs text-destructive">{msg.content}</p>}
    </div>
  );
}
